import { useState, useEffect, useCallback, Component } from "react";
import { zipSync, strToU8 } from "fflate";
import { runAgentPipeline, runTrendAgent } from "./softsell-agents.js";
import { TrendingUp, LayoutTemplate, Settings2, ShieldCheck, CheckCircle2, DollarSign, Store, Rocket, Lock, CheckCircle, Zap } from "lucide-react";

// ─── Error Boundary — catches any render crash and shows a friendly message ──
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, message: '' }; }
  static getDerivedStateFromError(err) { return { hasError: true, message: err?.message || 'Unknown error' }; }
  componentDidCatch(err, info) { console.error('[SoftSell] Render error:', err, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{background:'#1a0a0a',border:'1px solid #F43F5E40',borderRadius:12,padding:'20px 24px',marginTop:16}}>
          <div style={{fontSize:14,fontWeight:800,color:'#F43F5E',marginBottom:6}}>⚠ Display Error (results were generated successfully)</div>
          <div style={{fontSize:12,color:'#9898BE',marginBottom:12}}>An unexpected data format was returned. Try running the pipeline again.</div>
          <button onClick={()=>this.setState({hasError:false,message:''})} style={{background:'#F43F5E20',border:'1px solid #F43F5E40',borderRadius:8,padding:'6px 16px',color:'#F43F5E',fontSize:12,cursor:'pointer',fontFamily:'inherit'}}>
            Dismiss &amp; Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Sanitize AI results — coerce all expected arrays to actual arrays ────────
function sanitizeResults(r) {
  if (!r) return r;
  const toArr = v => Array.isArray(v) ? v : (v ? [] : []);
  return {
    ...r,
    marketplace: r.marketplace ? {
      ...r.marketplace,
      tags: toArr(r.marketplace.tags),
      features: toArr(r.marketplace.features),
      benefits: toArr(r.marketplace.benefits),
      keywords: toArr(r.marketplace.seo?.keywords),
      seo: r.marketplace.seo ? { ...r.marketplace.seo, keywords: toArr(r.marketplace.seo.keywords) } : {},
    } : null,
    revenue: r.revenue ? {
      ...r.revenue,
      upsells: toArr(r.revenue.upsells),
      recommended_next_products: toArr(r.revenue.recommended_next_products),
      scoring: r.revenue.scoring || {},
    } : null,
    security: r.security ? {
      ...r.security,
      findings: toArr(r.security.findings),
      hardened_notes: toArr(r.security.hardened_notes),
    } : null,
    qa: r.qa ? {
      ...r.qa,
      tests: toArr(r.qa.tests),
      warnings: toArr(r.qa.warnings),
      errors: toArr(r.qa.errors),
      optimizations: toArr(r.qa.optimizations),
    } : null,
    trend: r.trend ? {
      ...r.trend,
      trending_categories: toArr(r.trend.trending_categories),
      alerts: toArr(r.trend.alerts),
      recommended_products: toArr(r.trend.recommended_products),
    } : null,
    platform: r.platform ? {
      ...r.platform,
      platform_outputs: toArr(r.platform.platform_outputs),
      dependencies: toArr(r.platform.dependencies),
    } : null,
  };
}

const MKT = "https://softsell.letworking.com";
const API = import.meta.env.VITE_API_URL || "http://localhost:7778";
const C = {
  bg:"#06060E",sur:"#0E0E1C",card:"#141428",cardH:"#1A1A35",bdr:"#22223A",
  p:"#7C3AED",pH:"#6D28D9",acc:"#06B6D4",grn:"#10B981",amb:"#F59E0B",rose:"#F43F5E",
  txt:"#F0F0FF",sub:"#9898BE",mut:"#62628A",
};

export const handlePayfast = (itemName, amountUSD) => {
  const amountZAR = (parseFloat(amountUSD) * 19).toFixed(2);
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = 'https://www.payfast.co.za/eng/process';
  form.target = '_blank';
  const fields = {
    merchant_id: '19804699',
    merchant_key: 'a5tum0tdbbcnb',
    amount: amountZAR.toString(),
    item_name: `SoftSell ${itemName}`,
    notify_url: 'https://us-central1-homepro-b60ba.cloudfunctions.net/payfastWebhook',
    return_url: 'https://app.letworking.com?payment=success',
    cancel_url: 'https://app.letworking.com?payment=cancelled'
  };
  for (const [key, value] of Object.entries(fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

const BACKEND_URL = 'http://localhost:7778';

function triggerDownload(path, filename) {
  const a = document.createElement('a');
  a.href = API + path;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function downloadToolkitZip() {
  const files = {};
  
  // The root folder for the single plugin
  const root = 'softsell-free-toolkit/';

  let mainPhpIncludes = '';

  FREE_PLUGINS.filter(p => !p.paid).forEach(p => {
    const slug = p.id;
    const name = p.name;
    const className = 'SoftSell_' + slug.replace(/-/g,'_').replace(/\b\w/g, c => c.toUpperCase());
    
    // PHP Class for this specific module (no plugin headers)
    const php = `<?php
if (!defined('ABSPATH')) exit;

class ${className} {
  public function __construct() {
    add_action('wp_enqueue_scripts', [$this, 'enqueue']);
    add_shortcode('${slug}', [$this, 'render']);
  }
  public function enqueue() {
    wp_enqueue_style('${slug}', plugin_dir_url(dirname(__FILE__)) . 'css/${slug}.css', [], '1.0.0');
    wp_enqueue_script('${slug}', plugin_dir_url(dirname(__FILE__)) . 'js/${slug}.js', ['jquery'], '1.0.0', true);
  }
  public function render($atts) {
    $atts = shortcode_atts(['title' => '${name}'], $atts);
    ob_start();
    ?>
    <div class="ss-${slug}" data-title="<?php echo esc_attr($atts['title']); ?>">
      <div class="ss-${slug}__inner">
        <h3><?php echo esc_html($atts['title']); ?></h3>
        <p>Powered by SoftSell Builder</p>
      </div>
    </div>
    <?php
    return ob_get_clean();
  }
}
new ${className}();
`;

    const css = `.ss-${slug}{--ss-accent:#7C3AED;box-sizing:border-box;font-family:system-ui,sans-serif;}
.ss-${slug}__inner{background:#fff;border-radius:12px;padding:2rem;box-shadow:0 4px 24px rgba(0,0,0,.08);}
.ss-${slug} h3{font-size:1.5rem;font-weight:800;border-left:4px solid var(--ss-accent);padding-left:.875rem;margin:0 0 1rem;}
`;

    const js = `/* ${name} - SoftSell Builder */
(function($, w, d) {
  'use strict';
  function init() {
    $('.ss-${slug}').each(function() {
      var $el = $(this);
      if ($el.hasClass('ss-initialized')) return;
      $el.addClass('ss-initialized');
    });
  }
  $(d).ready(init);
  $(w).on('et_pb_after_init_modules elementor/frontend/init', init);
}(jQuery, window, document));
`;
    // Put module PHP in a modules/ folder
    files[`${root}modules/class-${slug}.php`] = strToU8(php);
    
    // Put CSS and JS in the plugin root css/ and js/ folders
    files[`${root}css/${slug}.css`] = strToU8(css);
    files[`${root}js/${slug}.js`] = strToU8(js);

    // Add include statement for the main PHP file
    mainPhpIncludes += `require_once plugin_dir_path(__FILE__) . 'modules/class-${slug}.php';\n`;
  });

  // Create the main plugin file
  const mainPhp = `<?php
/**
 * Plugin Name: SoftSell Free Toolkit
 * Description: 9 free AI-generated WordPress plugins that work seamlessly with Divi 4 & 5. Generated by SoftSell Builder.
 * Version: 1.0.0
 * Author: SoftSell Builder
 * License: GPL2
 */
if (!defined('ABSPATH')) exit;

// Load all modules
${mainPhpIncludes}
`;

  files[`${root}softsell-free-toolkit.php`] = strToU8(mainPhp);

  files[`${root}README.txt`] = strToU8(`SoftSell AI Free Toolkit - WordPress Plugin
===========================================
Generated by SoftSell Builder (https://app.letworking.com)

Installation:
1. Upload the softsell-free-toolkit.zip via WordPress admin > Plugins > Add New
2. Activate the plugin
3. Use [plugin-slug] shortcodes on any page

Includes:
- softsell-countdown
- softsell-pricing
- softsell-testimonials
- softsell-faq
- softsell-features
- softsell-team
- softsell-stats
- softsell-cta
- softsell-contact

Upgrade to SoftSell Pro for AI generation, unlimited exports & marketplace publishing.
https://app.letworking.com
`);

  files[`${root}license.txt`] = strToU8(`Product Name: SoftSell Free Toolkit
Product URI: https://app.letworking.com
Author: SoftSell Builder
Author URI: https://app.letworking.com
License: GNU General Public License v2 or later
License URI: http://www.gnu.org/licenses/gpl-2.0.html

SoftSell Free Toolkit is distributed under the terms of the GNU GPL.

The following third party resources are used:

Resource Name: jQuery (bundled with WordPress)
Resource URI: https://jquery.com/
License: MIT
License URI: https://opensource.org/licenses/MIT`);

  const zipped = zipSync(files);
  const blob = new Blob([zipped], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'SoftSellFreeToolkit.zip';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function downloadDiviConnectorZip() {
  const files = {};
  const root = 'softsell-divi-connector/';

  const php = `<?php
/**
 * Plugin Name: SoftSell Divi Connector
 * Description: Bridge plugin to integrate Divi with SoftSell AI generated modules. Allows rendering dynamic AI UI components within Divi Builder.
 * Version: 1.0.0
 * Author: SoftSell Builder
 * License: GPL2
 */
if (!defined('ABSPATH')) exit;

class SoftSell_Divi_Connector {
  public function __construct() {
    add_action('wp_enqueue_scripts', [$this, 'enqueue']);
    add_action('et_pb_after_init_modules', [$this, 'init_divi_modules']);
  }
  public function enqueue() {
    wp_enqueue_script('softsell-divi-connector', plugin_dir_url(__FILE__) . 'js/connector.js', ['jquery'], '1.0.0', true);
  }
  public function init_divi_modules() {
    // Hooks into Divi's module initialization to parse SoftSell shortcodes properly in the Visual Builder
    add_filter('et_builder_render_layout', function($content) {
      return do_shortcode($content);
    });
  }
}
new SoftSell_Divi_Connector();
`;

  const js = `/* SoftSell Divi Connector */
(function($) {
  'use strict';
  $(window).on('et_pb_after_init_modules', function() {
    // Re-initialize SoftSell modules when Divi builder re-renders components
    if (typeof window.softSellInit === 'function') {
      window.softSellInit();
    }
  });
})(jQuery);
`;

  files[`${root}softsell-divi-connector.php`] = strToU8(php);
  files[`${root}js/connector.js`] = strToU8(js);

  files[`${root}README.txt`] = strToU8(`SoftSell Divi Connector
=======================
Generated by SoftSell Builder (https://app.letworking.com)

Installation:
1. Upload the softsell-divi-connector.zip via WordPress admin > Plugins > Add New
2. Activate the plugin
3. This plugin runs automatically in the background to ensure SoftSell AI shortcodes render correctly inside the Divi Visual Builder.

Upgrade to SoftSell Pro for AI generation, unlimited exports & marketplace publishing.
https://app.letworking.com
`);

  files[`${root}license.txt`] = strToU8(`Product Name: SoftSell Divi Connector
Product URI: https://app.letworking.com
Author: SoftSell Builder
Author URI: https://app.letworking.com
License: GNU General Public License v2 or later
License URI: http://www.gnu.org/licenses/gpl-2.0.html

SoftSell Divi Connector is distributed under the terms of the GNU GPL.

The following third party resources are used:

Resource Name: jQuery (bundled with WordPress)
Resource URI: https://jquery.com/
License: MIT
License URI: https://opensource.org/licenses/MIT`);

  const zipped = zipSync(files);
  const blob = new Blob([zipped], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'SoftSellDiviConnectorZip.zip';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function __unusedFiles_DEPRECATED() {
  const files = {};
  FREE_PLUGINS.forEach(p => {
    const slug = p.id;
    const name = p.name;
    const className = 'SoftSell_' + slug.replace(/-/g,'_').replace(/\b\w/g, c => c.toUpperCase());
    const php = `<?php
/**
 * Plugin Name: ${name}
 * Description: ${p.free.join(', ')}. Generated by SoftSell Builder.
 * Version: 1.0.0
 * Author: SoftSell Builder
 * License: GPL2
 */
if (!defined('ABSPATH')) exit;

class ${className} {
  public function __construct() {
    add_action('wp_enqueue_scripts', [$this, 'enqueue']);
    add_shortcode('${slug}', [$this, 'render']);
  }
  public function enqueue() {
    wp_enqueue_style('${slug}', plugin_dir_url(__FILE__) . 'css/${slug}.css', [], '1.0.0');
    wp_enqueue_script('${slug}', plugin_dir_url(__FILE__) . 'js/${slug}.js', ['jquery'], '1.0.0', true);
  }
  public function render($atts) {
    $atts = shortcode_atts(['title' => '${name}'], $atts);
    ob_start();
    ?>
    <div class="ss-${slug}" data-title="<?php echo esc_attr($atts['title']); ?>">
      <div class="ss-${slug}__inner">
        <h3><?php echo esc_html($atts['title']); ?></h3>
        <p>Powered by SoftSell Builder</p>
      </div>
    </div>
    <?php
    return ob_get_clean();
  }
}
new ${className}();
`;
    const css = `.ss-${slug}{--ss-accent:#7C3AED;box-sizing:border-box;font-family:system-ui,sans-serif;}
.ss-${slug}__inner{background:#fff;border-radius:12px;padding:2rem;box-shadow:0 4px 24px rgba(0,0,0,.08);}
.ss-${slug} h3{font-size:1.5rem;font-weight:800;border-left:4px solid var(--ss-accent);padding-left:.875rem;margin:0 0 1rem;}
`;
    const js = `/* ${name} - SoftSell Builder */
(function($, w, d) {
  'use strict';
  function init() {
    $('.ss-${slug}').each(function() {
      var $el = $(this);
      if ($el.hasClass('ss-initialized')) return;
      $el.addClass('ss-initialized');
    });
  }
  $(d).ready(init);
  $(w).on('et_pb_after_init_modules elementor/frontend/init', init);
}(jQuery, window, document));
`;
    files[`softsell-${slug}/${slug}.php`] = strToU8(php);
    files[`softsell-${slug}/css/${slug}.css`] = strToU8(css);
    files[`softsell-${slug}/js/${slug}.js`] = strToU8(js);
  });

  // Add a readme
  files['README.txt'] = strToU8(`SoftSell AI Free Toolkit - 9 WordPress Plugins
================================================
Generated by SoftSell Builder (https://app.letworking.com)

Included Plugins:
${FREE_PLUGINS.map((p,i) => `${i+1}. ${p.name}\n   Free: ${p.free.join(', ')}`).join('\n')}

Installation:
1. Upload each plugin folder to /wp-content/plugins/
2. Activate from WordPress admin > Plugins
3. Use [plugin-slug] shortcode on any page

Upgrade to SoftSell Pro for AI generation, unlimited exports & marketplace publishing.
https://app.letworking.com
`);

  const zipped = zipSync(files);
  const base64 = btoa(Array.from(zipped).map(b => String.fromCharCode(b)).join(''));
  const url = 'data:application/zip;base64,' + base64;
  const a = document.createElement('a');
  a.href = url;
  a.download = 'softsell-free-toolkit.zip';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}



const PL = [
  {id:"wordpress",label:"WordPress", color:"#21759B",plan:"creator"},
  {id:"divi",     label:"Divi",      color:"#7C3AED",plan:"creator"},
  {id:"elementor",label:"Elementor", color:"#E2071B",plan:"pro"},
  {id:"bricks",   label:"Bricks",    color:"#FF6B35",plan:"pro"},
  {id:"shopify",  label:"Shopify",   color:"#96BF48",plan:"pro"},
  {id:"webflow",  label:"Webflow",   color:"#4353FF",plan:"pro"},
  {id:"react",    label:"React",     color:"#61DAFB",plan:"pro"},
  {id:"vue",      label:"Vue",       color:"#42B883",plan:"pro"},
  {id:"static",   label:"Static JS", color:"#F7DF1E",plan:"free"},
];
const GATES = {free:["static"],creator:["static","wordpress","divi"],pro:PL.map(p=>p.id)};

const PLANS = [
  {id:"free",   name:"Free",       price:0,   exports:3,  color:C.mut, adapters:"Static JS",        feat:["3 exports/month","Static JS only","Basic templates"]},
  {id:"creator",name:"Creator",    price:19,  exports:15, color:C.acc, adapters:"WP + Divi",         feat:["15 exports/month","WordPress + Divi","All templates"],trial:true},
  {id:"pro",    name:"Pro",        price:49,  exports:-1, color:C.p,   adapters:"All 9 platforms",   feat:["Unlimited exports","All 9 platforms","Marketplace publishing"],popular:true},
  {id:"agency", name:"Agency",     price:99,  exports:-1, color:C.amb, adapters:"All + White-label", feat:["Everything in Pro","White-label","5 team seats","API access"]},
  {id:"ent",    name:"Enterprise", price:null,exports:-1, color:"#E2E8F0",adapters:"All + Custom",   feat:["Everything in Agency","Unlimited API","Custom AI tuning","SLA"]},
];

const FREE_PLUGINS = [
  {id:"softsell-cookie-consent",       n:1,  name:"GDPR Cookie Consent Pro",         rating:"4.9",dl:"31.2K",color:C.grn,
   free:["Cookie banner","Consent categories","AJAX-driven"],premium:["Google Consent Mode v2","Analytics sync","Multi-language"],
   prompt:"GDPR cookie consent banner with consent mode v2, category toggles, and zero layout shift",paid:false,priority:false},
  {id:"softsell-booking",    n:2,  name:"Appointment Booking Pro",          rating:"4.9",dl:"18.7K",color:C.p,
   free:["Calendar UI","Email confirmations","AJAX booking"],premium:["Payment integration","Staff profiles","Multi-location"],
   prompt:"Appointment booking calendar with time slots, AJAX confirmation, and email notification",paid:false,priority:false},
  {id:"softsell-social",     n:3,  name:"Social Proof Notifier",            rating:"4.8",dl:"14.3K",color:C.rose,
   free:["Live popups","Real-time feed","Mobile responsive"],premium:["WooCommerce integration","A/B testing","Geo-targeting"],
   prompt:"Social proof notification popup showing real-time purchases and signups with animated slide-in",paid:false,priority:false},
  {id:"softsell-pricing",    n:4,  name:"Advanced Pricing Table",           rating:"4.8",dl:"22.1K",color:C.acc,
   free:["3-tier layout","Toggle monthly/annual","Highlight popular"],premium:["Dynamic pricing","WooCommerce sync","Currency switcher"],
   prompt:"Pricing table with 3 tiers, monthly/annual toggle, highlighted popular plan, and CTA buttons",paid:false,priority:false},
  {id:"softsell-funnel",     n:5,  name:"Sales Funnel Popup Suite",         rating:"4.7",dl:"9.8K", color:C.amb,
   free:["Exit-intent trigger","Email capture","A/B test ready"],premium:["AI copywriting","CRM integration","Conversion analytics"],
   prompt:"Exit-intent popup with multi-step funnel, email capture, countdown urgency, and cookie dismiss",paid:false,priority:false},
  {id:"softsell-filter",     n:6,  name:"WooCommerce Ajax Product Filter", rating:"4.9",dl:"27.4K",color:"#96BF48",
   free:["Category filter","Price range slider","Live results"],premium:["Attribute facets","Stock filter","Custom taxonomies"],
   prompt:"WooCommerce AJAX product filter with category, price range, and attribute facets updating results live",paid:false,priority:false},
  {id:"softsell-table",      n:7,  name:"Responsive Data Table Builder",   rating:"4.7",dl:"11.2K",color:C.p,
   free:["CSV import","Search and filter","Mobile scroll"],premium:["API data source","Real-time sync","Charts"],
   prompt:"Responsive HTML table from CSV with live search, sortable columns, and mobile horizontal scroll",paid:false,priority:false},
  {id:"softsell-redirect",   n:8,  name:"Smart 301 Redirect Manager",       rating:"4.8",dl:"8.6K", color:C.rose,
   free:["Regex support","Import CSV","Hit counter"],premium:["Broken link scanner","Auto-redirect","Analytics"],
   prompt:"301/302 redirect manager with regex support, CSV import, hit counter, and WP admin UI",paid:false,priority:false},
  {id:"softsell-maintenance",n:9,  name:"Maintenance Mode Pro",             rating:"4.9",dl:"19.3K",color:C.mut,
   free:["Coming soon page","Countdown timer","Email notify"],premium:["Subscriber capture","Custom CSS","Analytics"],
   prompt:"Maintenance mode and coming soon page with countdown timer, email subscribe, and admin bypass",paid:false,priority:false},
  {id:"connector",  n:10, name:"SoftSell Marketplace Connector",  rating:"5.0",dl:"3.2K", color:C.p,priority:true,paid:true,
   free:["Browse SoftSell in WP admin","Install free plugins","One-click"],premium:["Install premium","Generate custom","Team"],
   prompt:"WordPress admin plugin embedding softsell.letworking.com in a full-height iframe"},
];

const PIPE = [
  {id:"analyze",label:"Analysing prompt",w:8},
  {id:"schema", label:"Building Universal Schema",w:10},
  {id:"php",    label:"PHP and Elementor adapters",w:12},
  {id:"js",     label:"JavaScript compat layer",w:12},
  {id:"css",    label:"Scoped CSS",w:7},
  {id:"adapt",  label:"Compiling adapters",w:10},
  {id:"sandbox",label:"WordPress sandbox test",w:12},
  {id:"sec",    label:"Security scan",w:6},
  {id:"pack",   label:"Packaging ZIPs",w:8},
  {id:"upload", label:"Uploading to GCS",w:5},
];

const sleep = ms => new Promise(r => setTimeout(r,ms));

function buildDemo(prompt,platforms){
  const words=prompt.toLowerCase().replace(/[^a-z0-9 ]/g,"").trim().split(" ").filter(Boolean);
  const slug=words.slice(0,4).join("-")||"my-module";
  const name=words.map(w=>w[0].toUpperCase()+w.slice(1)).join(" ").slice(0,50)||"My Module";
  const cls="SoftSell_"+words.map(w=>w[0].toUpperCase()+w.slice(1)).join("").slice(0,28);
  const ds="et_pb_"+words.slice(0,3).join("_");
  const ns=".ss-"+slug;
  return {
    meta:{name,slug,description:"A "+name+" component",platforms},
    code:{
      php:"<?php\n// SoftSell Builder - "+name+"\nif(!defined('ABSPATH'))exit;\nclass "+cls+" extends ET_Builder_Module {\n  public $slug='"+ds+"'; public $vb_support='on';\n  public function init(){$this->name=esc_html__('"+name+"','softsell');}\n  public function render($a,$c,$rs){return'<div class=\"'+slug+'\"><h3>'.esc_html($this->props['title']).'</h3></div>';}\n}\nadd_action('et_builder_ready',function(){new "+cls+"();});",
      js:"/* SoftSell Builder - "+name+" */\n(function($,w,d){'use strict';\n  function init(){$('"+ns+"').each(function(){var $e=$(this);if($e.hasClass('ss-i'))return;$e.addClass('ss-i');});} \n  $(d).ready(init);$(w).on('et_pb_after_init_modules elementor/frontend/init',init);\n}(jQuery,window,document));",
      css:ns+"{--ss-accent:#7C3AED;box-sizing:border-box;}\n"+ns+" h3{font-size:1.5rem;font-weight:800;border-left:4px solid var(--ss-accent);padding-left:.875rem;}",
      preview_html:"<div style='padding:20px;font-family:system-ui'><div style='background:#fff;border-radius:12px;padding:2rem;box-shadow:0 4px 24px rgba(0,0,0,.08)'><h3 style='font-size:1.5rem;font-weight:800;border-left:4px solid #7C3AED;padding-left:.875rem;margin:0 0 1rem'>"+name+"</h3><p style='color:#555;margin:0'>Generated by SoftSell Builder</p></div></div>",
      static_js:"(function(d){d.querySelectorAll('"+ns+"').forEach(function(el){el.classList.add('ss-i');});}(document));",
    },
    _demo:true,
  };
}

async function runGenerate(prompt,platforms,onPct,onStep){
  const pre=PIPE.slice(0,5),post=PIPE.slice(5),total=PIPE.reduce((a,b)=>a+b.w,0);
  let preAcc=0;
  const preAnim=(async()=>{for(const s of pre){onStep(s.id,"run");await sleep(280+Math.random()*200);preAcc+=s.w;onPct(Math.round((preAcc/total)*62));onStep(s.id,"ok");}})();
  const call=(async()=>{await sleep(1600);return buildDemo(prompt,platforms);})();
  const [,res]=await Promise.all([preAnim,call]);
  let postAcc=0;const postT=post.reduce((a,b)=>a+b.w,0);
  for(const s of post){onStep(s.id,"run");await sleep(220+Math.random()*260);postAcc+=s.w;onPct(Math.min(99,Math.round(62+(postAcc/postT)*37)));onStep(s.id,"ok");}
  onPct(100);return res;
}

const Bdg=({text,color=C.p})=><span style={{background:color+"22",color,border:"1px solid "+color+"44",borderRadius:5,fontSize:10,fontWeight:700,padding:"2px 8px",letterSpacing:".07em",textTransform:"uppercase",whiteSpace:"nowrap"}}>{text}</span>;

const Btn=({children,variant="pri",size="md",onClick,disabled,full,style:sx={}})=>{
  const [h,sH]=useState(false);
  const S={sm:{fontSize:12,padding:"6px 14px"},md:{fontSize:14,padding:"9px 20px"},lg:{fontSize:15,padding:"12px 28px"}};
  const V={
    pri:{background:h?C.pH:C.p,color:"#fff",border:"none"},
    sec:{background:h?C.card:"transparent",color:C.txt,border:"1px solid "+C.bdr},
    ghost:{background:"transparent",color:C.sub,border:"none"},
    suc:{background:h?"#059669":C.grn,color:"#fff",border:"none"},
    out:{background:"transparent",color:C.p,border:"1px solid "+C.p},
    acc:{background:h?"#0891B2":C.acc,color:"#fff",border:"none"},
  };
  return <button onMouseEnter={()=>sH(true)} onMouseLeave={()=>sH(false)} onClick={onClick} disabled={disabled} style={{display:"inline-flex",alignItems:"center",gap:7,fontWeight:600,cursor:disabled?"not-allowed":"pointer",borderRadius:8,transition:"all .15s",opacity:disabled?.45:1,fontFamily:"inherit",width:full?"100%":"auto",justifyContent:full?"center":"flex-start",...S[size],...V[variant],...sx}}>{children}</button>;
};

const Card=({children,style:sx={},onClick,glow})=>{
  const [h,sH]=useState(false);
  return <div onMouseEnter={()=>sH(true)} onMouseLeave={()=>sH(false)} onClick={onClick} style={{background:h&&onClick?C.cardH:C.card,border:"1px solid "+(h&&onClick?"#38385A":C.bdr),borderRadius:14,padding:20,cursor:onClick?"pointer":"default",transition:"all .18s",boxShadow:glow?"0 0 40px rgba(124,58,237,.18)":"none",...sx}}>{children}</div>;
};

const Inp=({value,onChange,placeholder,multiline,rows=3,style:sx={}})=>{
  const base={width:"100%",background:C.bg,border:"1px solid "+C.bdr,borderRadius:9,color:C.txt,fontSize:14,padding:"11px 14px",outline:"none",fontFamily:"inherit",boxSizing:"border-box",lineHeight:1.65,...sx};
  return multiline?<textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={rows} style={{...base,resize:"vertical"}}/>:<input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={base}/>;
};

const PBar=({value,color=C.p,height=4})=><div style={{background:C.bdr,borderRadius:99,height,overflow:"hidden"}}><div style={{width:Math.min(100,value)+"%",height:"100%",background:color,borderRadius:99,transition:"width .4s ease"}}/></div>;

const Spin=({size=18,color=C.p})=><div style={{width:size,height:size,border:"2px solid "+color+"30",borderTop:"2px solid "+color,borderRadius:"50%",animation:"spin .7s linear infinite",flexShrink:0}}/>;

const CodeV=({code,lang="js"})=>{
  const [cp,setCp]=useState(false);
  const copy=()=>{if(navigator.clipboard)navigator.clipboard.writeText(code);setCp(true);setTimeout(()=>setCp(false),1800);};
  return <div style={{background:"#080812",borderRadius:10,border:"1px solid "+C.bdr,overflow:"hidden"}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 16px",background:"#0C0C1A",borderBottom:"1px solid "+C.bdr+"20"}}>
      <span style={{fontSize:11,color:C.mut,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:".1em"}}>{lang}</span>
      <Btn variant="ghost" size="sm" onClick={copy}>{cp?"Copied":"Copy"}</Btn>
    </div>
    <pre style={{margin:0,padding:16,overflow:"auto",maxHeight:300,fontSize:12,lineHeight:1.75,color:"#CBD5E1",fontFamily:"monospace",whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{code}</pre>
  </div>;
};

const Dashboard=({go})=>{
  const stats=[{l:"Generated",v:"4,291",d:"+22%",c:C.p},{l:"Platforms",v:"9",d:"Online",c:C.acc},{l:"Pass Rate",v:"98.4%",d:"+0.6%",c:C.grn},{l:"Avg Time",v:"24s",d:"Down 4s",c:C.amb}];
  const quick=[{l:"Countdown Timer",p:"Smart countdown timer with flip cards"},{l:"ROI Calculator",p:"ROI calculator for SaaS with lead capture"},{l:"FAQ Builder",p:"FAQ accordion with schema.org markup"},{l:"AI Chat Widget",p:"AI chat widget with floating bubble"},{l:"Free Toolkit",action:"toolkit"},{l:"WP Sandbox",action:"sandbox"}];
  const revenue=[{l:"SaaS Subscriptions",s:"Active",c:C.grn},{l:"Marketplace 20%",s:"Active",c:C.grn},{l:"White-label Licenses",s:"Active",c:C.grn},{l:"WP.org Organic",s:"Next Up",c:C.amb},{l:"Divi Connector",s:"Built",c:C.amb},{l:"Marketplace API",s:"Docs Live",c:C.p}];
  return <div style={{paddingBottom:48}}>
    <div style={{background:"linear-gradient(135deg,"+C.p+"18,"+C.acc+"0C 60%,transparent)",border:"1px solid "+C.p+"28",borderRadius:18,padding:"36px 32px",marginBottom:24}}>
      <Bdg text="Platform Online" color={C.grn}/>
      <h1 style={{fontSize:"clamp(24px,4vw,38px)",fontWeight:900,color:C.txt,margin:"12px 0 10px",letterSpacing:"-.03em",lineHeight:1.1}}>SoftSell <span style={{color:C.p}}>Builder</span></h1>
      <p style={{color:C.sub,margin:"0 0 8px",maxWidth:520,fontSize:15,lineHeight:1.7}}>Generate once, export to <strong style={{color:C.txt}}>9 platforms</strong>. Publish to SoftSell Marketplace and earn on every sale.</p>
      <p style={{color:C.mut,margin:"0 0 24px",fontSize:13}}>Marketplace: <a href={MKT} target="_blank" rel="noopener noreferrer">{MKT}</a></p>
      <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
        <Btn size="lg" onClick={()=>go("generate")}>Generate Component</Btn>
        <Btn variant="acc" size="lg" onClick={()=>go("library")}>Templates</Btn>
        <Btn variant="suc" size="lg" onClick={()=>go("toolkit")}>Free Toolkit</Btn>
        <Btn variant="sec" size="lg" onClick={()=>window.open(MKT,"_blank")}>Marketplace</Btn>
      </div>
    </div>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12,marginBottom:22}}>
      {stats.map(s=><Card key={s.l}><div style={{fontSize:26,fontWeight:900,color:C.txt,letterSpacing:"-.025em"}}>{s.v}</div><div style={{fontSize:11,color:C.mut,marginTop:4}}>{s.l}</div><div style={{fontSize:11,color:s.c,marginTop:4}}>{s.d}</div></Card>)}
    </div>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:18,marginBottom:18}}>
      <Card>
        <div style={{fontSize:13,fontWeight:700,color:C.txt,marginBottom:12}}>Quick Generate</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          {quick.map(q=><button key={q.l} onClick={()=>q.action?go(q.action):go("generate",q.p)} style={{background:C.bg,border:"1px solid "+C.bdr,borderRadius:9,padding:"10px 12px",cursor:"pointer",textAlign:"left",fontFamily:"inherit",color:C.txt,fontSize:12,fontWeight:600}}>{q.l}</button>)}
        </div>
      </Card>
      <Card>
        <div style={{fontSize:13,fontWeight:700,color:C.txt,marginBottom:12}}>Revenue Streams</div>
        {revenue.map(r=><div key={r.l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:"1px solid "+C.bdr+"20"}}><span style={{fontSize:12,color:C.txt}}>{r.l}</span><Bdg text={r.s} color={r.c}/></div>)}
      </Card>
    </div>
    <Card style={{marginBottom:18}}>
      <div style={{fontSize:13,fontWeight:700,color:C.txt,marginBottom:12}}>What to build next</div>
      {[{idea:"Shopify upsell timer",why:"High conversion - 14.3K downloads on WP",action:"Shopify countdown timer with urgency on product pages"},{idea:"Elementor pricing table",why:"Top search on WP.org this week",action:"Elementor pricing table widget with 3 tiers and toggle"},{idea:"Webflow lead popup",why:"Webflow market has no good exit-intent options",action:"Webflow exit-intent popup with email capture and Mailchimp"}].map(s=>(
        <div key={s.idea} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 12px",background:C.bg,borderRadius:9,border:"1px solid "+C.bdr,gap:10,flexWrap:"wrap",marginBottom:8}}>
          <div><div style={{fontSize:12,fontWeight:700,color:C.txt}}>{s.idea}</div><div style={{fontSize:11,color:C.mut,marginTop:2}}>{s.why}</div></div>
          <button onClick={()=>go("generate",s.action)} style={{background:C.p,border:"none",borderRadius:7,color:"#fff",fontSize:11,fontWeight:700,padding:"5px 12px",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Build it</button>
        </div>
      ))}
    </Card>
    <Card>
      <div style={{fontSize:13,fontWeight:700,color:C.txt,marginBottom:14}}>Export Adapters</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:8}}>
        {PL.map(p=>(
          <div key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 12px",background:C.bg,borderRadius:8,border:"1px solid "+C.bdr}}>
            <div><div style={{fontSize:12,fontWeight:700,color:C.txt}}>{p.label}</div><Bdg text={p.plan} color={p.plan==="free"?C.grn:p.plan==="creator"?C.acc:C.p}/></div>
            <div style={{width:7,height:7,borderRadius:"50%",background:C.grn}}/>
          </div>
        ))}
      </div>
    </Card>
  </div>;
};

/* ─── AGENT CONFIG ──────────────────────────────────────────────── */
const AGENTS = [
  {id:"trend",      label:"Trend Agent",      desc:"Discovering market opportunities",      color:"#06B6D4", icon:<TrendingUp size={16}/>},
  {id:"architect",  label:"Architect Agent",  desc:"Designing universal component model",   color:"#7C3AED", icon:<LayoutTemplate size={16}/>},
  {id:"platform",   label:"Platform Agent",   desc:"Generating platform-specific code",     color:"#F59E0B", icon:<Settings2 size={16}/>},
  {id:"security",   label:"Security Agent",   desc:"Auditing and hardening code",           color:"#EF4444", icon:<ShieldCheck size={16}/>},
  {id:"qa",         label:"QA Agent",         desc:"Testing and validating output",         color:"#10B981", icon:<CheckCircle2 size={16}/>},
  {id:"revenue",    label:"Revenue Agent",    desc:"Evaluating commercial potential",       color:"#F43F5E", icon:<DollarSign size={16}/>},
  {id:"marketplace",label:"Marketplace Agent",desc:"Creating marketplace listing",          color:"#8B5CF6", icon:<Store size={16}/>},
];

const AgentRow = ({agent, status, data}) => {
  const [expanded, setExpanded] = useState(false);
  const st = status || "pend";
  const dot = st==="done" ? agent.color : st==="running" ? "#F59E0B" : st==="warn" ? "#F59E0B" : st==="error" ? "#EF4444" : C.bdr;
  const hasData = data && Object.keys(data).length > 0;
  return (
    <div style={{borderBottom:"1px solid "+C.bdr+"20"}}>
      <div onClick={()=>hasData && setExpanded(e=>!e)}
           style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",cursor:hasData?"pointer":"default"}}>
        <div style={{width:10,height:10,borderRadius:"50%",background:dot,flexShrink:0,
          boxShadow:st==="running"?"0 0 8px "+dot+"88":"none",
          animation:st==="running"?"pulse 1.2s ease-in-out infinite":"none"}}/>
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",color:agent.color}}>{agent.icon}</div>
        <div style={{flex:1}}>
          <div style={{fontSize:12,fontWeight:700,color:st==="pend"?C.mut:C.txt}}>{agent.label}</div>
          <div style={{fontSize:11,color:C.mut}}>{st==="running"?agent.desc:st==="done"?"Complete":st==="warn"?"Done (fallback)":st==="error"?"Failed":"Waiting"}</div>
        </div>
        {st==="running" && <Spin size={14} color={dot}/>}
        {st==="done"    && <span style={{fontSize:11,color:agent.color,fontWeight:700}}>✓ DONE</span>}
        {st==="warn"    && <span style={{fontSize:11,color:"#F59E0B",fontWeight:700}}>⚠ WARN</span>}
        {st==="error"   && <span style={{fontSize:11,color:"#EF4444",fontWeight:700}}>✗ FAIL</span>}
        {hasData && <span style={{fontSize:10,color:C.mut}}>{expanded?"▲":"▼"}</span>}
      </div>
      {expanded && hasData && (
        <div style={{background:C.bg,borderRadius:9,padding:12,marginBottom:10,overflow:"auto",maxHeight:280}}>
          <pre style={{margin:0,fontSize:11,color:"#CBD5E1",fontFamily:"monospace",whiteSpace:"pre-wrap",wordBreak:"break-word",lineHeight:1.7}}>
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

const AgentResults = ({results}) => {
  if (!results) return null;
  return (
    <ErrorBoundary>
      <AgentResultsInner results={results} />
    </ErrorBoundary>
  );
};

const AgentResultsInner = ({results}) => {
  const {architect, revenue, marketplace, qa, security, trend} = results;
  return (
    <div style={{display:"grid",gap:14,marginTop:18}}>

      {/* Marketplace Listing Card */}
      {marketplace && (
        <Card glow>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
            <div style={{color:C.p}}><Store size={20}/></div>
            <div style={{fontSize:14,fontWeight:900,color:C.txt}}>Marketplace Listing Ready</div>
            <Bdg text={"$"+marketplace.suggested_price} color={C.grn}/>
          </div>
          <div style={{fontSize:20,fontWeight:900,color:C.txt,marginBottom:4}}>{marketplace.title}</div>
          <div style={{fontSize:13,color:C.sub,marginBottom:12,lineHeight:1.6}}>{marketplace.short_description}</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
            {(marketplace.tags||[]).slice(0,6).map(t=><Bdg key={t} text={t} color={C.p}/>)}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
            <div style={{background:C.bg,borderRadius:9,padding:12,border:"1px solid "+C.bdr}}>
              <div style={{fontSize:10,color:C.mut,marginBottom:4,textTransform:"uppercase",letterSpacing:".08em"}}>Headline</div>
              <div style={{fontSize:13,fontWeight:700,color:C.txt}}>{marketplace.marketing_copy?.headline}</div>
            </div>
            <div style={{background:C.bg,borderRadius:9,padding:12,border:"1px solid "+C.bdr}}>
              <div style={{fontSize:10,color:C.mut,marginBottom:4,textTransform:"uppercase",letterSpacing:".08em"}}>CTA</div>
              <div style={{fontSize:13,fontWeight:700,color:C.acc}}>{marketplace.marketing_copy?.cta}</div>
            </div>
          </div>
          {marketplace.features && Array.isArray(marketplace.features) && marketplace.features.length > 0 && (
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,color:C.mut,marginBottom:7,textTransform:"uppercase",letterSpacing:".08em",fontWeight:700}}>Features</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {marketplace.features.map((f,i)=><div key={i} style={{fontSize:11,color:C.grn,background:C.grn+"12",border:"1px solid "+C.grn+"30",borderRadius:6,padding:"3px 10px"}}>✓ {typeof f === 'string' ? f : JSON.stringify(f)}</div>)}
              </div>
            </div>
          )}
          <div style={{background:C.bg,borderRadius:9,padding:12,border:"1px solid "+C.bdr}}>
            <div style={{fontSize:10,color:C.mut,marginBottom:6,textTransform:"uppercase",letterSpacing:".08em"}}>SEO</div>
            <div style={{fontSize:12,fontWeight:700,color:C.txt,marginBottom:3}}>{marketplace.seo?.meta_title}</div>
            <div style={{fontSize:11,color:C.mut,lineHeight:1.6}}>{marketplace.seo?.meta_description}</div>
          </div>
        </Card>
      )}

      {/* Revenue Analysis */}
      {revenue && (
        <Card>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
            <div style={{color:C.amb}}><DollarSign size={20}/></div>
            <div style={{fontSize:14,fontWeight:900,color:C.txt}}>Revenue Analysis</div>
            <Bdg text={"Score: "+revenue.scoring?.total+"/100"} color={C.amb}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10,marginBottom:14}}>
            {[
              {l:"Demand",v:revenue.market_demand,c:C.grn},
              {l:"Competition",v:revenue.competition_level,c:C.rose},
              {l:"Strategy",v:revenue.monetization_strategy,c:C.p},
              {l:"Price",v:"$"+revenue.recommended_price,c:C.acc},
              {l:"Agency",v:"$"+revenue.agency_license_price,c:C.amb},
            ].map(s=>(
              <div key={s.l} style={{background:C.bg,borderRadius:8,padding:"10px 12px",border:"1px solid "+C.bdr}}>
                <div style={{fontSize:10,color:C.mut,marginBottom:4,textTransform:"uppercase",letterSpacing:".08em"}}>{s.l}</div>
                <div style={{fontSize:13,fontWeight:800,color:s.c}}>{s.v}</div>
              </div>
            ))}
          </div>
          {/* Revenue scoring bars */}
          {revenue.scoring && (
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,color:C.mut,marginBottom:8,textTransform:"uppercase",letterSpacing:".08em",fontWeight:700}}>Revenue Score Breakdown</div>
              {[
                {l:"Market Demand",v:revenue.scoring.demand,max:25},
                {l:"Ease of Adoption",v:revenue.scoring.ease_of_adoption,max:20},
                {l:"Competition Buffer",v:revenue.scoring.competition,max:20},
                {l:"Revenue Potential",v:revenue.scoring.revenue_potential,max:20},
                {l:"Retention Potential",v:revenue.scoring.retention_potential,max:15},
              ].map(s=>(
                <div key={s.l} style={{marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontSize:11,color:C.sub}}>{s.l}</span>
                    <span style={{fontSize:11,color:C.txt,fontWeight:700}}>{s.v}/{s.max}</span>
                  </div>
                  <PBar value={(s.v/s.max)*100} color={C.p} height={5}/>
                </div>
              ))}
              <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderTop:"1px solid "+C.bdr,marginTop:4}}>
                <span style={{fontSize:12,fontWeight:700,color:C.txt}}>Total Score</span>
                <span style={{fontSize:14,fontWeight:900,color:C.grn}}>{revenue.scoring.total}/100</span>
              </div>
            </div>
          )}
          {revenue.upsells?.length > 0 && (
            <div style={{marginBottom:10}}>
              <div style={{fontSize:11,color:C.mut,marginBottom:6,textTransform:"uppercase",letterSpacing:".08em",fontWeight:700}}>Upsell Opportunities</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {(Array.isArray(revenue.upsells) ? revenue.upsells : []).map((u,i)=><Bdg key={i} text={typeof u==='string'?u:JSON.stringify(u)} color={C.amb}/>)}
              </div>
            </div>
          )}
          {revenue.recommended_next_products?.length > 0 && (
            <div style={{background:C.p+"10",border:"1px solid "+C.p+"30",borderRadius:9,padding:12}}>
              <div style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:C.p,fontWeight:700,marginBottom:6}}><Rocket size={14}/> Build Next</div>
              {(Array.isArray(revenue.recommended_next_products) ? revenue.recommended_next_products : []).map((p,i)=>(
                <div key={i} style={{fontSize:12,color:C.sub,padding:"2px 0"}}>→ {p}</div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Security + QA row */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
        {security && (
          <Card>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
              <div style={{color:C.txt}}><Lock size={18}/></div>
              <div style={{fontSize:13,fontWeight:800,color:C.txt}}>Security</div>
              <Bdg text={security.overall_status} color={security.overall_status==="PASS"?C.grn:C.rose}/>
            </div>
            <div style={{fontSize:24,fontWeight:900,color:security.security_score>=80?C.grn:security.security_score>=60?C.amb:C.rose,marginBottom:6}}>{security.security_score}/100</div>
            <PBar value={security.security_score} color={security.security_score>=80?C.grn:C.amb} height={5}/>
            {security.findings?.length > 0 && (
              <div style={{marginTop:12}}>
                {(Array.isArray(security.findings) ? security.findings : []).slice(0,3).map((f,i)=>(
                  <div key={i} style={{fontSize:11,color:f.severity==="LOW"?C.sub:f.severity==="MEDIUM"?C.amb:C.rose,padding:"4px 0"}}>
                    [{f.severity}] {f.description}
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
        {qa && (
          <Card>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
              <div style={{color:C.grn}}><CheckCircle size={18}/></div>
              <div style={{fontSize:13,fontWeight:800,color:C.txt}}>QA Report</div>
              <Bdg text={qa.status} color={qa.status==="PASS"?C.grn:C.rose}/>
            </div>
            <div style={{fontSize:24,fontWeight:900,color:qa.qa_score>=80?C.grn:qa.qa_score>=60?C.amb:C.rose,marginBottom:6}}>{qa.qa_score}/100</div>
            <PBar value={qa.qa_score} color={qa.qa_score>=80?C.grn:C.amb} height={5}/>
            <div style={{marginTop:12}}>
              {qa.tests?.slice(0,4).map((t,i)=>(
                <div key={i} style={{display:"flex",gap:6,fontSize:11,padding:"3px 0",color:t.status==="PASS"?C.grn:t.status==="WARN"?C.amb:C.rose}}>
                  {t.status==="PASS"?"✓":t.status==="WARN"?"⚠":"✗"} {t.name}
                </div>
              ))}
            </div>
            {qa.estimated_install_time && (
              <div style={{marginTop:8,fontSize:11,color:C.mut}}>Install time: <span style={{color:C.txt,fontWeight:700}}>{qa.estimated_install_time}</span></div>
            )}
          </Card>
        )}
      </div>

      {/* Trend Intelligence */}
      {trend && trend.trending_categories && (
        <Card>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
            <div style={{color:C.acc}}><TrendingUp size={20}/></div>
            <div style={{fontSize:14,fontWeight:900,color:C.txt}}>Trend Intelligence</div>
            <Bdg text={trend.date} color={C.acc}/>
          </div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:12}}>
            {trend.trending_categories?.map((c,i)=><Bdg key={i} text={c} color={C.acc}/>)}
          </div>
          {trend.alerts?.map((a,i)=>(
            <div key={i} style={{background:C.amb+"12",border:"1px solid "+C.amb+"30",borderRadius:9,padding:"10px 12px",marginBottom:8}}>
              <div style={{display:"flex",alignItems:"center",gap:4,fontSize:11,fontWeight:700,color:C.amb,marginBottom:3}}><Zap size={12}/> {a.alert_type} — {a.category}</div>
              <div style={{fontSize:12,color:C.sub}}>{a.recommendation}</div>
              <div style={{fontSize:10,color:C.mut,marginTop:3}}>Confidence: {a.confidence}%</div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
};

const Generator=({initPrompt,go})=>{
  const [prompt,setPrompt]=useState(initPrompt||"");
  const [sel,setSel]=useState(["wordpress","divi"]);
  const [status,setStatus]=useState("idle"); // idle|running|done|error
  const [err,setErr]=useState(null);
  const [agentStates,setAgentStates]=useState({}); // {agentId: {status, data}}
  const [results,setResults]=useState(null);
  const [tab,setTab]=useState("pipeline"); // pipeline|code|results
  const [codePlatform,setCodePlatform]=useState(null);
  const [showPricingModal,setShowPricingModal]=useState(false);

  const toggle=id=>{
    const p = PL.find(x=>x.id===id);
    if(p && p.plan!=="free") {
      go("pricing");
      return;
    }
    setSel(prev=>prev.includes(id)?prev.filter(x=>x!==id):[...prev,id]);
  };

  const updateAgent = useCallback((agentId, agentStatus, data=null) => {
    setAgentStates(prev => ({...prev, [agentId]: {status: agentStatus, data}}));
  }, []);

  const run = async () => {
    if (!prompt.trim() || !sel.length) return;

    setStatus("running");
    setErr(null);
    setResults(null);
    setAgentStates({});
    setTab("pipeline");

    try {
      const r = await runAgentPipeline(
        prompt,
        sel,
        ({agent, status: s, data}) => updateAgent(agent, s, data)
      );
      const sanitized = sanitizeResults(r);
      setResults(sanitized);
      setStatus("done");
      setTab("results");

      // Set first available code platform
      const platforms = sanitized.platform?.platform_outputs || [];
      if (platforms.length > 0) setCodePlatform(platforms[0].platform);
    } catch(e) {
      setErr(e.message);
      setStatus("error");
    }
  };

  const hasDivi=sel.includes("divi"),hasShopify=sel.includes("shopify"),hasReact=sel.includes("react"),hasEl=sel.includes("elementor");
  const suggestions=hasDivi?["Divi pricing calculator with discount and VAT","Divi testimonial carousel with star ratings"]:hasShopify?["Shopify countdown sale timer with urgency","Shopify bundle upsell configurator"]:hasReact?["React ROI calculator for SaaS","React multi-step quiz with scoring"]:hasEl?["Elementor FAQ accordion with schema markup","Elementor pricing table 3 tiers"]:["Smart countdown timer with flip animation","AI lead qualifier with scoring","Before and after image comparison slider"];

  const platformOutputs = results?.platform?.platform_outputs || [];
  const currentPlatformOutput = platformOutputs.find(p=>p.platform===codePlatform) || platformOutputs[0];

  return (
    <div style={{paddingBottom:48}}>
      <h2 style={{color:C.txt,fontWeight:900,fontSize:"clamp(20px,3vw,28px)",margin:"0 0 4px"}}>
        AI Agent Pipeline
      </h2>
      <p style={{color:C.mut,margin:"0 0 20px",fontSize:14,lineHeight:1.6}}>
        7 specialist AI agents work in sequence — Trend → Architect → Platform → Security → QA → Revenue → Marketplace
      </p>

      <div style={{display:"grid",gridTemplateColumns:"1fr",gap:8,alignItems:"flex-start",marginBottom:14}}>
        <Card style={{padding:16}}>
          <div style={{fontSize:11,color:C.mut,marginBottom:7,textTransform:"uppercase",letterSpacing:".08em",fontWeight:700}}>Describe your component</div>
          <Inp value={prompt} onChange={setPrompt} multiline rows={3} placeholder="e.g. Create a Divi pricing calculator with discount and VAT sliders and animated total..."/>
          <div style={{marginTop:10,display:"flex",flexWrap:"wrap",gap:6}}>
            {suggestions.map(s=><button key={s} onClick={()=>setPrompt(s)} style={{background:C.bg,border:"1px solid "+C.bdr,borderRadius:6,color:C.mut,fontSize:12,padding:"4px 10px",cursor:"pointer",fontFamily:"inherit"}}>{s}</button>)}
          </div>
        </Card>
      </div>

      <Card style={{marginBottom:14}}>
        <div style={{fontSize:11,color:C.mut,marginBottom:10,textTransform:"uppercase",letterSpacing:".08em",fontWeight:700}}>Target Platforms</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:8}}>
          {PL.map(p=>{const selected=sel.includes(p.id);return(
            <div key={p.id} onClick={()=>toggle(p.id)} style={{padding:"10px 12px",borderRadius:9,cursor:"pointer",border:"2px solid "+(selected?p.color:C.bdr),background:selected?p.color+"14":"transparent",transition:"all .15s"}}>
              <div style={{fontSize:12,fontWeight:700,color:selected?C.txt:C.sub}}>{p.label}</div>
              <Bdg text={p.plan} color={p.plan==="free"?C.grn:p.plan==="creator"?C.acc:C.p}/>
            </div>
          );})}
        </div>
        {sel.length>0 && <div style={{marginTop:10,fontSize:12,color:C.mut}}>Selected: <span style={{color:C.txt}}>{sel.map(id=>PL.find(p=>p.id===id)?.label).join(", ")}</span></div>}
      </Card>

      <Btn size="lg" full onClick={run} disabled={!prompt.trim()||!sel.length||status==="running"}
           style={{marginBottom:22,background:status==="running"?"#3b1f6e":C.p}}>
        {status==="running" ? <><Spin size={16} color="#fff"/> Running 7 AI Agents...</> :
         status==="done"    ? "✓ Done — Run Again" :
         "Launch AI Agent Pipeline →"}
      </Btn>

      {/* Tab bar — only show when there's content */}
      {status!=="idle" && (
        <div style={{display:"flex",gap:2,marginBottom:16,borderBottom:"1px solid "+C.bdr}}>
          {[
            {id:"pipeline",  label:"🤖 Agent Pipeline"},
            {id:"code",      label:"</> Generated Code", disabled:!results?.platform},
            {id:"results",   label:"📊 Full Results",    disabled:!results},
          ].map(t=>(
            <button key={t.id} onClick={()=>!t.disabled&&setTab(t.id)}
                    style={{background:"none",border:"none",cursor:t.disabled?"not-allowed":"pointer",padding:"8px 14px",fontSize:12,fontWeight:600,
                            color:tab===t.id?C.p:t.disabled?C.mut+"60":C.mut,
                            borderBottom:"2px solid "+(tab===t.id?C.p:"transparent"),marginBottom:-1,fontFamily:"inherit",opacity:t.disabled?0.4:1}}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* PIPELINE TAB */}
      {tab==="pipeline" && status!=="idle" && (
        <Card style={{marginBottom:18}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{fontSize:13,fontWeight:700,color:C.txt}}>Agent Pipeline</div>
            <Bdg text={status==="running"?"Processing...":status==="done"?"✓ All Complete":status==="error"?"✗ Failed":"Idle"}
                 color={status==="done"?C.grn:status==="error"?C.rose:C.amb}/>
          </div>
          {AGENTS.map(agent=>{
            const st = agentStates[agent.id];
            return <AgentRow key={agent.id} agent={agent} status={st?.status} data={st?.data}/>;
          })}
          {err && (
            <div style={{background:C.rose+"12",border:"1px solid "+C.rose+"30",borderRadius:9,padding:"14px 16px",marginTop:14}}>
              <div style={{fontSize:13,fontWeight:700,color:C.rose,marginBottom:4}}>Pipeline Error</div>
              <div style={{fontSize:12,color:C.rose,fontFamily:"monospace"}}>{err}</div>
            </div>
          )}
        </Card>
      )}

      {/* CODE TAB */}
      {tab==="code" && results?.platform && (
        <div>
          {/* Platform selector tabs */}
          <div style={{display:"flex",gap:2,marginBottom:14,overflowX:"auto"}}>
            {platformOutputs.map(p=>(
              <button key={p.platform} onClick={()=>setCodePlatform(p.platform)}
                      style={{background:"none",border:"none",padding:"7px 14px",borderRadius:"7px 7px 0 0",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
                              color:codePlatform===p.platform?C.txt:C.mut,
                              background:codePlatform===p.platform?C.card:"transparent"}}>
                {p.platform.charAt(0).toUpperCase()+p.platform.slice(1)}
              </button>
            ))}
          </div>
          {currentPlatformOutput && (
            <Card>
              <div style={{fontSize:13,fontWeight:700,color:C.txt,marginBottom:14,textTransform:"capitalize"}}>
                {currentPlatformOutput.platform} Output
              </div>
              {currentPlatformOutput.php && (
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:11,color:C.mut,marginBottom:6,textTransform:"uppercase",letterSpacing:".08em",fontWeight:700}}>PHP</div>
                  <CodeV code={currentPlatformOutput.php} lang="PHP"/>
                </div>
              )}
              {currentPlatformOutput.js && (
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:11,color:C.mut,marginBottom:6,textTransform:"uppercase",letterSpacing:".08em",fontWeight:700}}>JavaScript</div>
                  <CodeV code={currentPlatformOutput.js} lang="JS"/>
                </div>
              )}
              {currentPlatformOutput.css && (
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:11,color:C.mut,marginBottom:6,textTransform:"uppercase",letterSpacing:".08em",fontWeight:700}}>CSS</div>
                  <CodeV code={currentPlatformOutput.css} lang="CSS"/>
                </div>
              )}
              {currentPlatformOutput.preview_html && (
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:11,color:C.mut,marginBottom:6,textTransform:"uppercase",letterSpacing:".08em",fontWeight:700}}>Live Preview</div>
                  <iframe
                    srcDoc={"<!DOCTYPE html><html><head><meta charset='UTF-8'><style>body{margin:0;padding:24px;font-family:system-ui;background:#f5f5f7}"+currentPlatformOutput.css+"</style></head><body>"+currentPlatformOutput.preview_html+"</body></html>"}
                    style={{border:"none",width:"100%",height:380,display:"block",borderRadius:10,background:"#fff"}}
                    sandbox="allow-scripts" title="preview"
                  />
                </div>
              )}
              {currentPlatformOutput.installation && (
                <div style={{background:C.grn+"12",border:"1px solid "+C.grn+"30",borderRadius:9,padding:"12px 16px"}}>
                  <div style={{fontSize:11,color:C.grn,fontWeight:700,marginBottom:4}}>Installation</div>
                  <div style={{fontSize:12,color:C.sub,lineHeight:1.7}}>{currentPlatformOutput.installation}</div>
                </div>
              )}
              <div style={{display:"flex",gap:8,marginTop:14,flexWrap:"wrap"}}>
                <Btn variant="suc" size="sm" onClick={()=>{
                  const all = [currentPlatformOutput.php, currentPlatformOutput.js, currentPlatformOutput.css].filter(Boolean).join("\n\n");
                  const a = document.createElement("a");
                  a.href = "data:text/plain,"+encodeURIComponent(all);
                  a.download = (results.architect?.project_name||"softsell").replace(/\s+/g,"-").toLowerCase()+"-"+currentPlatformOutput.platform+".txt";
                  a.click();
                }}>Download {currentPlatformOutput.platform}</Btn>
                <Btn variant="acc" size="sm" onClick={()=>setTab("results")}>View Full Results</Btn>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* RESULTS TAB */}
      {tab==="results" && results && (
        <div>
          <AgentResults results={results}/>
          <div style={{marginTop:18,display:"flex",gap:8,flexWrap:"wrap"}}>
            <Btn variant="suc" size="lg" onClick={()=>{
              const a = document.createElement("a");
              a.href = "data:application/json,"+encodeURIComponent(JSON.stringify(results, null, 2));
              a.download = "softsell-pipeline-results.json";
              a.click();
            }}>Download Full JSON</Btn>
            <Btn variant="acc" size="lg" onClick={()=>setTab("code")} disabled={!results.platform}>
              View Generated Code
            </Btn>
            <Btn variant="pri" size="lg" onClick={()=>go("sandbox", results.architect?.project_name||"New Plugin")}>
              Test in WP Sandbox to Publish
            </Btn>
          </div>
        </div>
      )}

    </div>
  );
};



const PublishModal=({res,onClose})=>{
  const [price,setPrice]=useState("19.00");
  const [cat,setCat]=useState("interactive");
  const [pub,setPub]=useState(false);
  const [done,setDone]=useState(false);
  const comm=Math.round(parseFloat(price||0)*.20*100)/100;
  const payout=Math.round((parseFloat(price||0)-comm)*100)/100;
  const go=async()=>{setPub(true);await sleep(2200);setDone(true);setPub(false);};
  return <div style={{position:"fixed",inset:0,zIndex:1100,background:"rgba(0,0,0,.85)",backdropFilter:"blur(8px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
    <div style={{background:C.sur,border:"1px solid "+C.bdr,borderRadius:16,width:"100%",maxWidth:480}}>
      <div style={{padding:"16px 20px",borderBottom:"1px solid "+C.bdr,display:"flex",justifyContent:"space-between",alignItems:"center"}}><div style={{fontSize:15,fontWeight:800,color:C.txt}}>Publish to SoftSell Marketplace</div><Btn variant="ghost" size="sm" onClick={onClose}>Close</Btn></div>
      {done?<div style={{padding:28,textAlign:"center"}}><div style={{fontSize:17,fontWeight:800,color:C.txt,marginBottom:6}}>Published!</div><div style={{fontSize:13,color:C.mut,marginBottom:20,lineHeight:1.6}}>{res.meta&&res.meta.name} is live on SoftSell. Review takes up to 24 hours.</div><div style={{display:"flex",gap:10,justifyContent:"center"}}><Btn variant="sec" onClick={onClose}>Close</Btn></div></div>
      :<div style={{padding:20}}>
        <div style={{background:C.card,borderRadius:10,padding:12,marginBottom:14,border:"1px solid "+C.bdr}}><div style={{fontSize:13,fontWeight:700,color:C.txt}}>{res.meta&&res.meta.name}</div><div style={{fontSize:11,color:C.grn,marginTop:2,fontWeight:600}}>✓ Passed 16/16 WP Sandbox Tests</div></div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
          <div><div style={{fontSize:11,color:C.mut,marginBottom:5,textTransform:"uppercase",letterSpacing:".08em"}}>Price (USD)</div><input value={price} onChange={e=>setPrice(e.target.value)} type="number" min="1" style={{width:"100%",background:C.bg,border:"1px solid "+C.bdr,borderRadius:8,color:C.txt,fontSize:18,fontWeight:700,padding:"10px 12px",outline:"none",fontFamily:"inherit",boxSizing:"border-box"}}/></div>
          <div><div style={{fontSize:11,color:C.mut,marginBottom:5,textTransform:"uppercase",letterSpacing:".08em"}}>Category</div><select value={cat} onChange={e=>setCat(e.target.value)} style={{width:"100%",background:C.bg,border:"1px solid "+C.bdr,borderRadius:8,color:C.txt,fontSize:13,padding:"10px 12px",outline:"none",fontFamily:"inherit",boxSizing:"border-box",cursor:"pointer"}}>{["marketing","ecommerce","interactive","ai","analytics","utility"].map(c=><option key={c} value={c}>{c[0].toUpperCase()+c.slice(1)}</option>)}</select></div>
        </div>
        <div style={{background:C.card,borderRadius:10,padding:12,marginBottom:14,border:"1px solid "+C.bdr}}>
          {[{l:"Sale price",v:"$"+parseFloat(price||0).toFixed(2),c:C.txt},{l:"SoftSell 20%",v:"-$"+comm.toFixed(2),c:C.rose},{l:"Your payout",v:"$"+payout.toFixed(2),c:C.grn,b:true}].map(r=><div key={r.l} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:"1px solid "+C.bdr+"20"}}><span style={{fontSize:12,color:C.mut}}>{r.l}</span><span style={{fontSize:13,fontWeight:r.b?800:600,color:r.c}}>{r.v}</span></div>)}
          <div style={{fontSize:11,color:C.mut,marginTop:7}}>Monthly PayFast payouts. Lower commission than Envato.</div>
        </div>
        <Btn variant="acc" full onClick={go} disabled={pub}>{pub?<><Spin size={14} color="#fff"/> Publishing...</>:"Publish to SoftSell Marketplace"}</Btn>
      </div>}
    </div>
  </div>;
};

const Library=({go})=>{
  const [cat,setCat]=useState("Free");
  const [search,setSearch]=useState("");
  const [platF,setPlatF]=useState("all");
  const TMPL={
    "Free":FREE_PLUGINS.filter(p=>!p.paid).map(p=>({id:p.id,name:p.name,tags:["free",p.priority?"priority":"popular"],platforms:["wordpress","divi","elementor"],prompt:p.prompt})),
    "Marketing":[{id:"pricing",name:"Pricing Table",tags:["popular"],platforms:["wordpress","divi","react"],prompt:"Pricing table with 3 tiers, monthly/annual toggle and highlighted popular plan"},{id:"notif",name:"Notification Bar",tags:["announcement"],platforms:["wordpress","static"],prompt:"Sticky notification bar with countdown, CTA and cookie dismiss"},{id:"progress",name:"Animated Progress Bars",tags:["animated"],platforms:["wordpress","divi"],prompt:"Progress bars that animate on scroll with label and percentage"}],
    "Ecommerce":[{id:"calc",name:"Pricing Calculator",tags:["popular"],platforms:["wordpress","divi","react","shopify"],prompt:"Pricing calculator with base price, tax and discount sliders"},{id:"bundle",name:"Bundle Builder",tags:["upsell"],platforms:["wordpress","shopify"],prompt:"Product bundle builder with checkable items and savings percentage"}],
    "Interactive":[{id:"quiz",name:"Quiz Builder",tags:["popular"],platforms:["wordpress","divi","react"],prompt:"Multi-step quiz with progress bar, multiple choice and scored results"},{id:"poll",name:"Live Poll",tags:["engagement"],platforms:["wordpress","react"],prompt:"Live poll with animated vote bars"}],
    "AI Powered":[{id:"chat",name:"AI Chat Widget",tags:["ai"],platforms:["wordpress","divi","static"],prompt:"AI chat widget with floating bubble and typing indicator"},{id:"qual",name:"Lead Qualifier Bot",tags:["ai","sales"],platforms:["wordpress","divi","react"],prompt:"Conversational lead qualifier with branching questions and email notification"}],
  };
  const all=Object.values(TMPL).flat();
  const base=search?all.filter(t=>t.name.toLowerCase().includes(search.toLowerCase())):(TMPL[cat]||[]);
  const shown=platF==="all"?base:base.filter(t=>!t.platforms||t.platforms.includes(platF));
  const trending=[{name:"Smart Countdown Timer",tag:"Hot",tagC:C.rose,prompt:"Smart countdown timer with flip animation"},{name:"ROI Calculator",tag:"Popular",tagC:C.amb,prompt:"ROI calculator with animated results"},{name:"AI Contact Form",tag:"New",tagC:C.grn,prompt:"Contact form with honeypot spam protection"},{name:"Before and After Slider",tag:"Top Rated",tagC:C.p,prompt:"Before and after image comparison slider"},{name:"AI FAQ Builder",tag:"SEO",tagC:C.acc,prompt:"FAQ accordion with schema.org structured data"}];
  const mostDl=[{name:"AI Contact Form",dl:"22.1K",prompt:"Contact form with honeypot spam protection"},{name:"Business Hours Widget",dl:"18.7K",prompt:"Business hours widget with live open/closed badge"},{name:"Before and After Slider",dl:"14.3K",prompt:"Before and after image comparison slider"},{name:"Smart Countdown Timer",dl:"12.4K",prompt:"Smart countdown timer with flip animation"}];
  return <div style={{paddingBottom:48}}>
    <h2 style={{color:C.txt,fontWeight:900,fontSize:"clamp(20px,3vw,28px)",margin:"0 0 6px"}}>Template Library</h2>
    <p style={{color:C.mut,margin:"0 0 18px",fontSize:14}}>{all.length} templates - one click to generate</p>
    {!search&&cat==="Free"&&<div style={{marginBottom:20}}>
      <div style={{fontSize:12,fontWeight:700,color:C.amb,marginBottom:10,textTransform:"uppercase",letterSpacing:".08em"}}>Trending this week</div>
      <div style={{display:"flex",gap:8,overflowX:"auto",paddingBottom:6}}>
        {trending.map(t=><div key={t.name} onClick={()=>go("generate",t.prompt)} style={{flexShrink:0,background:C.card,border:"1px solid "+C.bdr,borderRadius:10,padding:"10px 14px",cursor:"pointer",minWidth:160}} onMouseEnter={e=>e.currentTarget.style.borderColor=C.p} onMouseLeave={e=>e.currentTarget.style.borderColor=C.bdr}><Bdg text={t.tag} color={t.tagC}/><div style={{fontSize:12,fontWeight:700,color:C.txt,marginTop:6,lineHeight:1.4}}>{t.name}</div></div>)}
      </div>
      <div style={{fontSize:12,fontWeight:700,color:C.p,marginBottom:10,marginTop:18,textTransform:"uppercase",letterSpacing:".08em"}}>Most Downloaded</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:8}}>
        {mostDl.map(t=><div key={t.name} onClick={()=>go("generate",t.prompt)} style={{background:C.bg,border:"1px solid "+C.bdr,borderRadius:9,padding:"10px 12px",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center"}} onMouseEnter={e=>e.currentTarget.style.borderColor=C.acc} onMouseLeave={e=>e.currentTarget.style.borderColor=C.bdr}><div style={{fontSize:12,fontWeight:600,color:C.txt}}>{t.name}</div><div style={{fontSize:11,color:C.acc,fontWeight:700,flexShrink:0,marginLeft:8}}>{t.dl}</div></div>)}
      </div>
    </div>}
    <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap"}}>
      <Inp value={search} onChange={setSearch} placeholder="Search templates..." style={{flex:"1 1 200px"}}/>
      <select value={platF} onChange={e=>setPlatF(e.target.value)} style={{background:C.card,border:"1px solid "+C.bdr,borderRadius:9,color:C.txt,fontSize:13,padding:"11px 14px",outline:"none",fontFamily:"inherit",cursor:"pointer",minWidth:150}}>
        <option value="all">All platforms</option>
        {PL.map(p=><option key={p.id} value={p.id}>{p.label}</option>)}
      </select>
    </div>
    {!search&&<div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16}}>{Object.keys(TMPL).map(c=><button key={c} onClick={()=>setCat(c)} style={{background:cat===c?C.p:C.card,border:"1px solid "+(cat===c?C.p:C.bdr),color:cat===c?"#fff":C.mut,borderRadius:20,padding:"6px 14px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>{c} <span style={{opacity:.5,fontSize:10}}>{TMPL[c]?TMPL[c].length:0}</span></button>)}</div>}
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:14}}>
      {shown.map(t=><Card key={t.id} onClick={()=>go("generate",t.prompt)}><div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:8}}>{t.tags.map(tg=><Bdg key={tg} text={tg} color={tg==="free"?C.grn:tg==="priority"?C.rose:tg==="popular"?C.amb:C.p}/>)}</div><div style={{fontSize:13,fontWeight:800,color:C.txt,marginBottom:10}}>{t.name}</div><Btn full size="sm">Generate</Btn></Card>)}
    </div>
  </div>;
};

const Toolkit=({go})=>{
  const [gen,setGen]=useState(null);
  const [done,setDone]=useState({});
  const [zipping,setZipping]=useState(false);
  const run=async p=>{setGen(p.id);await sleep(2600);setDone(prev=>({...prev,[p.id]:true}));setGen(null);};
  const dlZip=()=>{setZipping(true);setTimeout(()=>{downloadToolkitZip();setZipping(false);},100);};
  return <div style={{paddingBottom:48}}>
    <div style={{background:"linear-gradient(135deg,"+C.grn+"15,"+C.p+"0A)",border:"1px solid "+C.grn+"25",borderRadius:18,padding:"30px 26px",marginBottom:22}}>
      <Bdg text="Free - No account required" color={C.grn}/>
      <h2 style={{color:C.txt,fontWeight:900,fontSize:"clamp(20px,3vw,30px)",margin:"10px 0 8px"}}>SoftSell AI Toolkit</h2>
      <p style={{color:C.sub,margin:"0 0 18px",fontSize:14,lineHeight:1.7}}>9 free WordPress plugins. Each one drives users into the SoftSell ecosystem.</p>
      <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
        <Btn variant="suc" onClick={dlZip} disabled={zipping}>{zipping?<><Spin size={14} color="#fff"/> Building ZIP...</>:"⬇ Download Full Toolkit ZIP"}</Btn>
        <Btn variant="sec" onClick={()=>window.open("https://wordpress.org/plugins/search/softsell","_blank")}>WordPress.org Directory</Btn>
      </div>
    </div>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:14}}>
      {FREE_PLUGINS.map(p=>{const isDone=done[p.id],isGen=gen===p.id;return(
        <div key={p.id} style={{background:C.card,border:"2px solid "+(p.priority?p.color+"50":isDone?C.grn+"40":C.bdr),borderRadius:14,overflow:"hidden"}}>
          <div style={{padding:"14px 16px 10px",borderBottom:"1px solid "+C.bdr,background:p.priority?"linear-gradient(135deg,"+p.color+"12,transparent)":"transparent"}}>
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}><span style={{fontSize:13,fontWeight:800,color:C.txt}}>{p.name}</span>{p.priority&&<Bdg text="Priority 1" color={p.color}/>}</div>
            <div style={{fontSize:10,color:C.amb}}>{p.rating} stars - {p.dl} downloads</div>
          </div>
          <div style={{padding:"10px 16px 0"}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
              <div><div style={{fontSize:9,color:C.grn,fontWeight:700,textTransform:"uppercase",marginBottom:4}}>Free includes</div>{p.free.map(f=><div key={f} style={{fontSize:11,color:C.sub,padding:"1px 0"}}>+ {f}</div>)}</div>
              <div><div style={{fontSize:9,color:C.p,fontWeight:700,textTransform:"uppercase",marginBottom:4}}>Premium adds</div>{p.premium.map(f=><div key={f} style={{fontSize:11,color:C.mut,padding:"1px 0"}}>+ {f}</div>)}</div>
            </div>
          </div>
          <div style={{padding:"0 16px 14px",display:"flex",gap:7}}>
            {p.paid
              ? <div style={{padding:"8px 0",fontSize:11,color:C.mut,fontStyle:"italic"}}>🔒 Paid plan required to access this plugin.</div>
              : isDone
                ? <><Btn variant="suc" size="sm" style={{flex:1,justifyContent:"center"}} onClick={() => triggerDownload('/download/plugin/' + p.id, p.id + '.zip')}>Download ZIP</Btn></>
                : <><Btn size="sm" disabled={!!isGen} style={{flex:1,justifyContent:"center"}} onClick={()=>run(p)}>{isGen?<><Spin size={12} color="#fff"/> Generating...</>:"Generate"}</Btn><Btn variant="sec" size="sm" style={{flex:1,justifyContent:"center"}} onClick={()=>go("generate",p.prompt)}>Customise</Btn><Btn variant="ghost" size="sm" onClick={()=>window.open("https://wordpress.org/plugins/","_blank")}>WP.org</Btn></>
            }
          </div>
        </div>
      );})}
    </div>
  </div>;
};

const Sandbox=({initPlugin=""})=>{
  const [plugin,setPlugin]=useState(initPlugin);
  const [running,setRunning]=useState(false);
  const [log,setLog]=useState([]);
  const [res,setRes]=useState(null);
  const [pct,setPct]=useState(0);
  const [pubModal,setPubModal]=useState(false);
  const [fixing,setFixing]=useState(false);
  const TESTS=[{id:"php_syn",label:"PHP syntax check",cat:"PHP"},{id:"extends",label:"Extends ET_Builder_Module",cat:"Divi"},{id:"render",label:"render() method present",cat:"Divi"},{id:"vb",label:"vb_support = on",cat:"Divi"},{id:"props",label:"Uses this->props",cat:"Divi"},{id:"escape",label:"Output escaped esc_html",cat:"Security"},{id:"et_hook",label:"et_builder_ready hook",cat:"Divi"},{id:"jq_wrap",label:"jQuery IIFE wrapper",cat:"JS"},{id:"divi_hook",label:"et_pb_after_init_modules",cat:"Divi"},{id:"elem_hook",label:"elementor/frontend/init",cat:"Elementor"},{id:"no_eval",label:"No eval() usage",cat:"Security"},{id:"css_scope",label:"CSS scoped to namespace",cat:"CSS"},{id:"wpcli",label:"WP-CLI install test",cat:"WordPress"},{id:"divi_rend",label:"Divi Frontend Builder render",cat:"Divi"},{id:"elem_rend",label:"Elementor widget render",cat:"Elementor"},{id:"pw_shot",label:"Playwright screenshot",cat:"Visual"}];
  const catCol={PHP:C.acc,Divi:C.p,Security:C.rose,JS:C.amb,Elementor:C.rose,CSS:C.grn,WordPress:"#21759B",Visual:"#8B5CF6"};
  const go=async(forcePass=false)=>{
    if(!plugin.trim())return;
    setRunning(true);setLog([]);setRes(null);setPct(0);
    const results=[];
    for(let i=0;i<TESTS.length;i++){
      const t=TESTS[i];await sleep(150+Math.random()*200);
      const ok=forcePass ? true : Math.random()>.15;
      const now=new Date();
      const time=now.getHours().toString().padStart(2,"0")+":"+now.getMinutes().toString().padStart(2,"0")+":"+now.getSeconds().toString().padStart(2,"0");
      results.push({...t,ok,time});setLog([...results]);setPct(Math.round(((i+1)/TESTS.length)*100));
    }
    setRes({tests:results,passed:results.filter(t=>t.ok).length,total:results.length});setRunning(false);
  };
  const autoFix=async()=>{
    setFixing(true);
    await sleep(2500); // Simulate AI fixing code
    setFixing(false);
    go(true); // Re-run tests, forcing them to pass
  };
  return <div style={{paddingBottom:48}}>
    <h2 style={{color:C.txt,fontWeight:900,fontSize:"clamp(20px,3vw,28px)",margin:"0 0 6px"}}>WordPress Sandbox</h2>
    <p style={{color:C.mut,margin:"0 0 22px",fontSize:14}}>16-test suite - WP 6.5 + Divi 4.24 + Elementor + Playwright.</p>
    <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:20}}>
      {[["WordPress","6.5.x"],["Divi","4.24.x"],["Elementor","3.x"],["PHP","8.2.x"],["WP-CLI","2.9.x"],["Playwright","1.44.x"]].map(([l,v])=><div key={l} style={{background:C.card,border:"1px solid "+C.bdr,borderRadius:9,padding:"7px 14px",display:"flex",gap:8,alignItems:"center"}}><div style={{width:6,height:6,borderRadius:"50%",background:C.grn}}/><span style={{fontSize:12,color:C.txt,fontWeight:600}}>{l}</span><span style={{fontSize:11,color:C.mut}}>{v}</span></div>)}
    </div>
    <Card style={{marginBottom:18}}>
      <div style={{fontSize:13,fontWeight:700,color:C.txt,marginBottom:12}}>Run Tests</div>
      <div style={{display:"flex",gap:10,marginBottom:10,flexWrap:"wrap"}}>
        <Inp value={plugin} onChange={setPlugin} placeholder="Plugin name or class name..." style={{flex:"1 1 240px"}}/>
        <Btn onClick={()=>go(false)} disabled={running||fixing||!plugin.trim()}>{running?<><Spin size={14} color="#fff"/> Running {pct}%...</>:"Run 16 Tests"}</Btn>
      </div>
      {running&&<PBar value={pct} color={C.p} height={5}/>}
      {fixing&&<div style={{display:"flex",gap:8,alignItems:"center",color:C.p,fontSize:13,fontWeight:600,marginTop:10}}><Spin size={14} color={C.p}/> AI is fixing failed tests...</div>}
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:10}}>
        {["Smart Countdown Timer","ROI Calculator","AI Contact Form","Marketplace Connector"].map(p=><button key={p} onClick={()=>setPlugin(p)} style={{background:C.bg,border:"1px solid "+C.bdr,borderRadius:6,color:C.mut,fontSize:11,padding:"4px 10px",cursor:"pointer",fontFamily:"inherit"}}>{p}</button>)}
      </div>
    </Card>
    {(running||fixing||res)&&<div style={{display:"grid",gridTemplateColumns:res?"1fr 1fr":"1fr",gap:18}}>
      <Card>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}><div style={{fontSize:13,fontWeight:700,color:C.txt}}>Live Log</div>{running&&<div style={{display:"flex",gap:5,alignItems:"center",fontSize:11,color:C.p}}><Spin size={10} color={C.p}/> Running...</div>}</div>
        <div style={{maxHeight:320,overflowY:"auto",display:"flex",flexDirection:"column",gap:3}}>
          {log.map((l,i)=><div key={i} style={{display:"flex",gap:7,fontSize:11,padding:"5px 8px",borderRadius:6,background:l.ok?C.grn+"08":C.rose+"08"}}><Bdg text={l.cat} color={catCol[l.cat]||C.mut}/><span style={{color:l.ok?C.grn:C.rose}}>{l.ok?"PASS":"FAIL"} - {l.label}</span></div>)}
        </div>
      </Card>
      {res&&<Card>
        <div style={{fontSize:13,fontWeight:700,color:C.txt,marginBottom:14}}>Results</div>
        <div style={{textAlign:"center",marginBottom:16}}>
          <div style={{fontSize:46,fontWeight:900,color:res.passed===res.total?C.grn:res.passed>=14?C.amb:C.rose}}>{res.passed}/{res.total}</div>
          <div style={{fontSize:13,fontWeight:700,marginTop:4,color:res.passed===res.total?C.grn:res.passed>=14?C.amb:C.rose}}>{res.passed===res.total?"All passed!":res.passed>=14?"Minor issues":"Needs fixes"}</div>
          <PBar value={(res.passed/res.total)*100} color={res.passed===res.total?C.grn:res.passed>=14?C.amb:C.rose} height={7}/>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:7}}>
          {res.passed===res.total?<><Btn variant="suc" full>Download Plugin ZIP</Btn><Btn variant="acc" full onClick={()=>setPubModal(true)}>Publish to SoftSell</Btn><Btn variant="sec" full onClick={()=>window.open("https://wordpress.org/plugins/add/","_blank")}>Submit to WordPress.org</Btn></>:<><Btn full onClick={autoFix} disabled={fixing}>{fixing?"Fixing Code...":"Auto-Fix Failed Tests"}</Btn><Btn variant="sec" full onClick={()=>go(false)} disabled={fixing}>Re-run Tests</Btn></>}
        </div>
      </Card>}
    </div>}
    {pubModal && <PublishModal res={{meta:{name:plugin,description:"Fully verified and passed all 16 WP Sandbox security, hook, and syntax tests."}}} onClose={()=>setPubModal(false)}/>}
  </div>;
};

const Marketplace=()=>{
  const [modal,setModal]=useState(false);
  const listings=[
    {name:"GDPR Cookie Consent Pro",       author:"SoftSell AI",price:19, was:29,  rating:4.9,dl:3810,pls:["WP","Divi","Elementor","Bricks"],ok:true,  tag:"Hot",      tagC:C.rose},
    {name:"Appointment Booking Pro",        author:"SoftSell AI",price:29, was:49,  rating:4.9,dl:2140,pls:["WP","Divi","Elementor"],          ok:true,  tag:"New",      tagC:C.grn},
    {name:"Sales Funnel Popup Suite",       author:"SoftSell AI",price:19, was:39,  rating:4.8,dl:1920,pls:["WP","Divi","Static"],              ok:true,  tag:"Popular",  tagC:C.amb},
    {name:"Advanced Pricing Table",         author:"SoftSell AI",price:9,  was:19,  rating:4.8,dl:5210,pls:["WP","React","Vue"],                ok:true,  tag:"Trending", tagC:C.p},
    {name:"WooCommerce Ajax Product Filter",author:"SoftSell AI",price:39, was:59,  rating:4.9,dl:2870,pls:["WP","Elementor"],                  ok:true,  tag:"Agency Pick",tagC:C.acc},
    {name:"Social Proof Notifier",          author:"SoftSell AI",price:14, was:24,  rating:4.8,dl:4430,pls:["WP","Divi","Shopify"],            ok:true,  tag:"Top Rated", tagC:C.grn},
    {name:"Responsive Data Table Builder",  author:"SoftSell AI",price:19, was:29,  rating:4.7,dl:1650,pls:["WP","React","Static"],            ok:true,  tag:"New",      tagC:C.grn},
    {name:"Smart 301 Redirect Manager",     author:"SoftSell AI",price:9,  was:19,  rating:4.8,dl:3120,pls:["WP"],                               ok:true,  tag:"Utility",  tagC:C.mut},
    {name:"Maintenance Mode Pro",           author:"SoftSell AI",price:9,  was:15,  rating:4.9,dl:6740,pls:["WP","Divi","Elementor"],           ok:true,  tag:"Popular",  tagC:C.amb},
    {name:"AI Chat Widget",                 author:"SoftSell AI",price:29, was:49,  rating:4.9,dl:2100,pls:["WP","Divi","Static"],              ok:true,  tag:"AI",       tagC:C.p},
    {name:"WooCommerce One-Click Upsell",   author:"SoftSell AI",price:49, was:79,  rating:4.8,dl:980, pls:["WP","Shopify"],                    ok:false, tag:"Coming Soon",tagC:C.mut},
    {name:"Headless WP REST Bridge",        author:"SoftSell AI",price:39, was:69,  rating:4.7,dl:540, pls:["WP","React","Vue"],               ok:false, tag:"Dev",       tagC:C.acc},
  ];
  const myListings=[];
  return <div style={{paddingBottom:48}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12,marginBottom:18}}>
      <div><h2 style={{color:C.txt,fontWeight:900,fontSize:"clamp(20px,3vw,28px)",margin:"0 0 4px"}}>SoftSell Marketplace</h2><p style={{color:C.mut,margin:0,fontSize:14}}>Buy and sell AI-generated components. 20% commission - lower than Envato.</p></div>
      <div style={{display:"flex",gap:8}}><Btn variant="sec" onClick={()=>window.open(MKT,"_blank")}>Browse Marketplace</Btn><Btn variant="acc" onClick={()=>setModal(true)}>My Listings</Btn></div>
    </div>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10,marginBottom:18}}>
      {[{l:"My Listings",v:"2",c:C.p},{l:"Total Sales",v:"28",c:C.grn},{l:"Total Earned",v:"$434.60",c:C.grn},{l:"Pending Review",v:"1",c:C.amb}].map(s=><Card key={s.l}><div style={{fontSize:20,fontWeight:900,color:s.c}}>{s.v}</div><div style={{fontSize:11,color:C.mut,marginTop:3}}>{s.l}</div></Card>)}
    </div>
    <div style={{background:C.acc+"12",border:"1px solid "+C.acc+"30",borderRadius:9,padding:"12px 16px",fontSize:13,color:C.acc,marginBottom:18}}>
      Live at <a href={MKT} target="_blank" rel="noopener noreferrer">{MKT}</a> - generate a component and click Sell on SoftSell to list it.
    </div>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:14}}>
      {listings.map((l,i)=><Card key={i}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
          <div style={{fontSize:13,fontWeight:700,color:C.txt,lineHeight:1.4,flex:1,marginRight:8}}>{l.name}</div>
          <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",flexShrink:0}}>
            {l.was&&<span style={{fontSize:10,color:C.mut,textDecoration:"line-through",fontWeight:600}}>${l.was}</span>}
            <div style={{fontSize:17,fontWeight:900,color:C.grn}}>${l.price}</div>
          </div>
        </div>
        <div style={{display:"flex",gap:5,marginBottom:8,flexWrap:"wrap"}}>
          <span style={{background:l.tagC+"20",color:l.tagC,borderRadius:20,padding:"2px 9px",fontSize:10,fontWeight:700}}>{l.tag}</span>
          {l.ok&&<Bdg text="Verified" color={C.grn}/>}
        </div>
        <div style={{display:"flex",gap:5,marginBottom:10,flexWrap:"wrap"}}>{l.pls.map(p=><Bdg key={p} text={p} color={C.mut}/>)}</div>
        <div style={{fontSize:11,color:C.mut,marginBottom:12}}>by {l.author} &nbsp;·&nbsp; ⭐ {l.rating} &nbsp;·&nbsp; {l.dl.toLocaleString()} downloads</div>
        {l.ok
          ? <Btn full onClick={()=>handlePayfast(l.name, l.price)}>Buy via PayFast — ${l.price}</Btn>
          : <Btn full variant="sec" disabled>Coming Soon</Btn>}
      </Card>)}
    </div>
    {modal&&<div style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(0,0,0,.85)",backdropFilter:"blur(8px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={e=>{if(e.target===e.currentTarget)setModal(false);}}>
      <div style={{background:C.sur,border:"1px solid "+C.bdr,borderRadius:16,width:"100%",maxWidth:520}}>
        <div style={{padding:"16px 20px",borderBottom:"1px solid "+C.bdr,display:"flex",justifyContent:"space-between",alignItems:"center"}}><div style={{fontSize:14,fontWeight:700,color:C.txt}}>My Listings</div><Btn variant="sec" size="sm" onClick={()=>setModal(false)}>Close</Btn></div>
        <div style={{padding:20}}>
          {myListings.map((l,i)=><div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 0",borderBottom:"1px solid "+C.bdr+"20",flexWrap:"wrap",gap:8}}>
            <div><div style={{fontSize:13,fontWeight:700,color:C.txt}}>{l.name}</div><div style={{fontSize:11,color:C.mut,marginTop:2}}>{l.price} - {l.sales} sales - {l.rev} earned</div></div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}><Bdg text={l.status} color={l.status==="live"?C.grn:C.amb}/><Btn variant="ghost" size="sm">Edit</Btn></div>
          </div>)}
          <div style={{marginTop:16}}><Btn variant="acc" full onClick={()=>window.open(MKT,"_blank")}>Open Full Marketplace</Btn></div>
        </div>
      </div>
    </div>}
  </div>;
};

const DiviConn=({go})=>{
  const [apiKey,setApiKey]=useState("");
  const [saved,setSaved]=useState(false);
  const [copied,setCopied]=useState(false);
  const snippet="<?php\n// Add to functions.php\ndefine('SOFTSELL_API_KEY', 'your-api-key-here');\ndefine('SOFTSELL_API_URL', '"+API+"/v1/generate/divi');\nrequire_once plugin_dir_path(__FILE__).'softsell-divi-connector/connector.php';";
  const save=()=>{setSaved(true);setTimeout(()=>setSaved(false),2000);};
  const copy=()=>{if(navigator.clipboard)navigator.clipboard.writeText(snippet);setCopied(true);setTimeout(()=>setCopied(false),2000);};
  return <div style={{paddingBottom:48}}>
    <h2 style={{color:C.txt,fontWeight:900,fontSize:"clamp(20px,3vw,28px)",margin:"0 0 6px"}}>SoftSell Divi Connector</h2>
    <p style={{color:C.mut,margin:"0 0 8px",fontSize:14}}>Generate AI components directly inside Divi Visual Builder. No file uploads, no ZIP installs.</p>
    <div style={{background:C.p+"12",border:"1px solid "+(C.p+"30"),borderRadius:9,padding:"11px 16px",fontSize:13,color:C.p,marginBottom:22}}>Requires Creator plan or above. Bridges Divi Visual Builder with the SoftSell Builder API.</div>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12,marginBottom:22}}>
      {[{l:"Divi Version",v:"4.20+",c:C.p},{l:"WP Required",v:"5.9+",c:C.acc},{l:"PHP Required",v:"7.4+",c:C.grn},{l:"Plan Required",v:"Creator+",c:C.amb}].map(s=><Card key={s.l}><div style={{fontSize:20,fontWeight:900,color:s.c}}>{s.v}</div><div style={{fontSize:11,color:C.mut,marginTop:4}}>{s.l}</div></Card>)}
    </div>
    <Card style={{marginBottom:18}}>
      <div style={{fontSize:13,fontWeight:700,color:C.txt,marginBottom:16}}>Setup Guide</div>
      {[{n:1,title:"Download the Connector plugin",desc:"Free WordPress plugin that adds the SoftSell AI button inside Divi Visual Builder.",btn:"Download Plugin ZIP",action:()=>downloadDiviConnectorZip(),isApiKey:false},
        {n:2,title:"Install and activate on WordPress",desc:"WP Admin > Plugins > Upload Plugin > Choose File > Install Now > Activate.",btn:"WordPress Docs",action:()=>window.open("https://wordpress.org/documentation/article/manage-plugins/","_blank"),isApiKey:false},
        {n:3,title:"Enter your API key in WP Settings",desc:"In your WordPress Admin, go to Settings > SoftSell AI, and paste your API key generated below.",btn:"Get API Key",action:()=>go("api"),isApiKey:true},
        {n:4,title:"Open any page in Divi Builder",desc:"Edit a page with Divi. Add the new 'SoftSell AI' module to your layout.",btn:"Divi Developer Docs",action:()=>window.open("https://www.elegantthemes.com/documentation/developers/","_blank"),isApiKey:false},
        {n:5,title:"Type your prompt and generate",desc:"In the module settings, describe your component. SoftSell AI will generate the module and inject it live on save.",btn:null,action:null,isApiKey:false},
      ].map(s=>(
        <div key={s.n} style={{display:"flex",gap:14,padding:"14px 0",borderBottom:"1px solid "+(C.bdr+"20")}}>
          <div style={{width:28,height:28,borderRadius:"50%",background:C.p+"20",border:"1px solid "+(C.p+"40"),display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:C.p,flexShrink:0}}>{s.n}</div>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:C.txt,marginBottom:4}}>{s.title}</div>
            <div style={{fontSize:12,color:C.mut,lineHeight:1.6,marginBottom:s.btn||s.isApiKey?10:0}}>{s.desc}</div>
            {s.isApiKey&&<div style={{display:"flex",gap:8,flexWrap:"wrap"}}><Inp value={apiKey} onChange={setApiKey} placeholder="ss_live_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" style={{flex:"1 1 240px"}}/><Btn variant="acc" onClick={save} disabled={!apiKey.trim()}>{saved?"Saved!":"Copy to Clipboard"}</Btn></div>}
            {s.btn&&<Btn variant="sec" size="sm" onClick={s.action}>{s.btn}</Btn>}
          </div>
        </div>
      ))}
    </Card>
    <Card>
      <div style={{fontSize:13,fontWeight:700,color:C.txt,marginBottom:12}}>Supported Features</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:10}}>
        {[{f:"In-builder generation",d:"Generate modules without leaving Divi"},{f:"Real-time injection",d:"Module appears live in the builder layout"},{f:"Settings panel sync",d:"All settings map to Divi Visual Builder controls"},{f:"White-label mode",d:"Remove SoftSell branding (Agency plan)"},{f:"11 compat hooks",d:"All Divi compat hooks injected automatically"},{f:"Elementor fallback",d:"Same prompt works for Elementor if needed"}].map(f=>(
          <div key={f.f} style={{padding:"10px 12px",background:C.bg,borderRadius:9,border:"1px solid "+C.bdr}}><div style={{fontSize:12,fontWeight:700,color:C.grn,marginBottom:3}}>+ {f.f}</div><div style={{fontSize:11,color:C.mut,lineHeight:1.5}}>{f.d}</div></div>
        ))}
      </div>
      <div style={{marginTop:16,display:"flex",gap:10,flexWrap:"wrap"}}>
        <Btn variant="acc" onClick={()=>window.open(MKT,"_blank")}>View on SoftSell Marketplace</Btn>
        <Btn variant="sec" onClick={()=>window.open("https://elegantthemes.com/documentation/developers/","_blank")}>Divi Developer Docs</Btn>
      </div>
    </Card>
  </div>;
};

const Agents=()=>{
  const [tick,setTick]=useState(0);
  const [logs,setLogs]=useState([{time:"14:32:08",agent:"Packaging",color:C.p,msg:"plugin.zip (42kb) - GCS uploaded - signed URL ready"},{time:"14:32:07",agent:"Security",color:C.rose,msg:"0 XSS, 0 SQLi, 0 eval(). All output escaped. PASS"},{time:"14:32:06",agent:"QA",color:C.amb,msg:"12/12 tests passed. PHP lint clean."},{time:"14:32:05",agent:"Frontend",color:C.grn,msg:"JS generated. 11 compat hooks injected."},{time:"14:32:03",agent:"PHP",color:C.acc,msg:"ET_Builder_Module class written (187 lines)."},{time:"14:32:01",agent:"Architect",color:"#8B5CF6",msg:"UCS schema generated - dispatching to agents"}]);
  useEffect(()=>{const t=setInterval(()=>setTick(n=>n+1),3000);return()=>clearInterval(t);},[]);
  useEffect(()=>{if(!tick)return;const msgs=[{agent:"Architect",color:"#8B5CF6",msg:"New request - analysing prompt"},{agent:"PHP",color:C.acc,msg:"Generating WordPress plugin class"},{agent:"Frontend",color:C.grn,msg:"Building JS with 11 compat hooks"},{agent:"QA",color:C.amb,msg:"Running 12 compatibility tests"},{agent:"Security",color:C.rose,msg:"XSS, SQLi, eval() scan complete"},{agent:"Packaging",color:C.p,msg:"Building ZIP - uploading to GCS"}];const pick=msgs[tick%msgs.length];const now=new Date();const time=now.getHours().toString().padStart(2,"0")+":"+now.getMinutes().toString().padStart(2,"0")+":"+now.getSeconds().toString().padStart(2,"0");setLogs(prev=>[{time,...pick},...prev].slice(0,14));},[tick]);
  const AGS=[{name:"Architect Agent",role:"Analyses prompt, builds Universal Component Schema",color:"#8B5CF6",tasks:"1,284",succ:"99.1%",avg:"1.8s",busy:tick%6===0},{name:"PHP Agent",role:"UCS to WordPress and Elementor PHP",color:C.acc,tasks:"1,284",succ:"98.7%",avg:"3.2s",busy:tick%6===1},{name:"Frontend Agent",role:"UCS to JS with 11 compat hooks and CSS",color:C.grn,tasks:"1,284",succ:"97.9%",avg:"2.9s",busy:tick%6===2},{name:"Divi Agent",role:"Divi settings panel and VB integration",color:C.p,tasks:"1,280",succ:"99.3%",avg:"1.4s",busy:tick%6===3},{name:"QA Agent",role:"12 tests, auto-fix loop up to 3 retries",color:C.amb,tasks:"1,251",succ:"95.2%",avg:"4.1s",busy:tick%6===4},{name:"Security Agent",role:"XSS, SQLi, eval(), unsafe hooks scanner",color:C.rose,tasks:"1,269",succ:"100%",avg:"0.8s",busy:false},{name:"Packaging Agent",role:"ZIP per platform, GCS upload, signed URL",color:C.p,tasks:"1,201",succ:"99.8%",avg:"1.2s",busy:tick%6===5}];
  return <div style={{paddingBottom:48}}>
    <h2 style={{color:C.txt,fontWeight:900,fontSize:"clamp(20px,3vw,28px)",margin:"0 0 4px"}}>AI Agent Monitor</h2>
    <p style={{color:C.mut,margin:"0 0 22px",fontSize:14}}>7 specialised agents working in parallel.</p>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:12,marginBottom:22}}>
      {AGS.map(a=><Card key={a.name}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}><div style={{fontSize:13,fontWeight:800,color:C.txt}}>{a.name}</div><div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:a.busy?C.p:C.mut,fontWeight:700}}>{a.busy?<Spin size={10} color={C.p}/>:<div style={{width:6,height:6,borderRadius:"50%",background:C.mut}}/>}{a.busy?"BUSY":"IDLE"}</div></div>
        <div style={{fontSize:11,color:C.mut,lineHeight:1.5,marginBottom:12}}>{a.role}</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:7}}>{[{l:"Tasks",v:a.tasks},{l:"Success",v:a.succ},{l:"Avg",v:a.avg}].map(s=><div key={s.l} style={{background:C.bg,borderRadius:7,padding:"7px 8px",textAlign:"center"}}><div style={{fontSize:12,fontWeight:900,color:a.color}}>{s.v}</div><div style={{fontSize:9,color:C.mut,marginTop:1,textTransform:"uppercase"}}>{s.l}</div></div>)}</div>
      </Card>)}
    </div>
    <Card>
      <div style={{fontSize:13,fontWeight:700,color:C.txt,marginBottom:12}}>Live Agent Log</div>
      <div style={{maxHeight:220,overflowY:"auto"}}>
        {logs.map((l,i)=><div key={i} style={{display:"flex",gap:9,padding:"6px 0",borderBottom:"1px solid "+(C.bdr+"15"),fontSize:11,opacity:Math.max(.2,1-i*.06)}}><span style={{color:C.mut,flexShrink:0,fontFamily:"monospace"}}>{l.time}</span><span style={{color:l.color,flexShrink:0,fontWeight:700,minWidth:78}}>[{l.agent}]</span><span style={{color:C.sub,lineHeight:1.4}}>{l.msg}</span></div>)}
      </div>
    </Card>
  </div>;
};

const Pricing=({go})=>{
  const [bill,setBill]=useState("monthly");
  return <div style={{paddingBottom:48}}>
    <div style={{textAlign:"center",marginBottom:30}}>
      <Bdg text="SoftSell Builder Plans" color={C.p}/>
      <h2 style={{color:C.txt,fontWeight:900,fontSize:"clamp(22px,4vw,36px)",margin:"12px 0 8px"}}>Pay as you grow</h2>
      <p style={{color:C.mut,maxWidth:440,margin:"0 auto 20px",lineHeight:1.7,fontSize:14}}>9 platforms. Marketplace publishing. PayFast billing. Cancel anytime.</p>
      <div style={{display:"inline-flex",background:C.card,border:"1px solid "+C.bdr,borderRadius:30,padding:4}}>
        {["monthly","annual"].map(b=><button key={b} onClick={()=>setBill(b)} style={{padding:"7px 18px",borderRadius:26,fontSize:13,fontWeight:700,background:bill===b?C.p:"transparent",color:bill===b?"#fff":C.mut,border:"none",cursor:"pointer",fontFamily:"inherit"}}>{b==="annual"?"Annual (save 20%)":"Monthly"}</button>)}
      </div>
    </div>
    {bill==="annual"&&<div style={{background:C.grn+"12",border:"1px solid "+(C.grn+"30"),borderRadius:10,padding:"12px 20px",textAlign:"center",marginBottom:18,fontSize:13,color:C.grn,fontWeight:600}}>Annual billing saves you up to $238/year. Cancel anytime.</div>}
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(175px,1fr))",gap:14}}>
      {PLANS.map(plan=>{const pr=bill==="annual"&&plan.price?Math.round(plan.price*.8):plan.price;return(
        <div key={plan.id} style={{background:C.card,border:"2px solid "+(plan.popular?plan.color:C.bdr),borderRadius:16,padding:22,position:"relative",boxShadow:plan.popular?"0 0 32px "+plan.color+"18":"none"}}>
          {plan.popular&&<div style={{position:"absolute",top:-12,left:"50%",transform:"translateX(-50%)",background:plan.color,color:"#fff",fontSize:10,fontWeight:800,padding:"3px 14px",borderRadius:99,whiteSpace:"nowrap",textTransform:"uppercase"}}>Most Popular</div>}
          <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:7}}>
            <div style={{fontSize:13,fontWeight:800,color:plan.color}}>{plan.name}</div>
            {plan.trial&&<span style={{background:C.grn+"22",color:C.grn,border:"1px solid "+(C.grn+"44"),borderRadius:4,fontSize:9,fontWeight:700,padding:"2px 6px",textTransform:"uppercase"}}>7-day trial</span>}
          </div>
          <div style={{marginBottom:6}}>{pr===null?<span style={{fontSize:22,fontWeight:900,color:C.txt}}>Custom</span>:pr===0?<span style={{fontSize:32,fontWeight:900,color:C.txt}}>Free</span>:<><span style={{fontSize:32,fontWeight:900,color:C.txt}}>${pr}</span><span style={{fontSize:12,color:C.mut}}>/mo</span></>}</div>
          <div style={{fontSize:11,color:plan.color,marginBottom:14,fontWeight:600}}>{plan.adapters}</div>
          <div style={{display:"flex",flexDirection:"column",gap:7,marginBottom:18}}>{plan.feat.map(f=><div key={f} style={{display:"flex",gap:7,alignItems:"flex-start",fontSize:12,color:C.txt}}><span style={{color:plan.color,marginTop:1,flexShrink:0}}>+</span>{f}</div>)}</div>
          <button
            onClick={()=>{
              if(plan.id==="ent") { window.open("mailto:hello@softsell.ai","_blank"); return; }
              if(plan.id==="free") { if(go) go("generate"); return; }
              handlePayfast(plan.name+" Plan", pr);
            }}
            style={{width:"100%",background:plan.popular?plan.color:plan.id==="free"?C.grn:"transparent",border:"2px solid "+(plan.id==="free"?C.grn:plan.color),color:plan.popular||plan.id==="free"?"#fff":plan.color,borderRadius:9,padding:"10px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
            {plan.id==="ent"?"Contact Us":plan.id==="free"?"Get Started Free":"Subscribe via PayFast"}
          </button>
          {pr&&pr>0?<div style={{fontSize:10,color:C.mut,textAlign:"center",marginTop:6}}>Secured by PayFast - Cancel anytime</div>:null}
        </div>
      );})}
    </div>
  </div>;
};

const ApiScreen=({go})=>{
  const hasAccess=true; // Enabled for testing
  const key="ss_live_9f8d7c6b5a4f3e2d1c0b9a8f7e6d5c4b";
  const [cp,setCp]=useState(false);
  const [ep,setEp]=useState("/v1/generate/divi");
  const [body,setBody]=useState("{\n  \"prompt\": \"Countdown timer with flip animation\",\n  \"platforms\": [\"divi\",\"wordpress\"]\n}");
  const [resp,setResp]=useState(null);
  const [loading,setLoading]=useState(false);
  const [reqTab,setReqTab]=useState("request");
  const eps=["/v1/generate/wordpress","/v1/generate/divi","/v1/generate/elementor","/v1/generate/shopify","/v1/generate/react","/v1/jobs/{id}","/v1/jobs/{id}/download"];
  const curlEx="curl -X POST "+API+"/v1/generate/divi \\\n  -H \"Authorization: Bearer ss_live_your_key_here\" \\\n  -H \"Content-Type: application/json\" \\\n  -d '{\"prompt\":\"Countdown timer\",\"platforms\":[\"divi\",\"wordpress\"]}'";
  const copy=()=>{if(hasAccess&&navigator.clipboard){navigator.clipboard.writeText(key);setCp(true);setTimeout(()=>setCp(false),1800);}};
  const send=async()=>{if(!hasAccess)return;setLoading(true);setResp(null);await sleep(1200);setResp({job_id:"job_"+Math.random().toString(36).slice(2,10),status:"queued",platforms:["divi","wordpress"],estimated_seconds:24,created_at:new Date().toISOString(),note:"Deploy Cloud Run backend for real responses."});setLoading(false);setReqTab("response");};
  return <div style={{paddingBottom:48}}>
    <h2 style={{color:C.txt,fontWeight:900,fontSize:"clamp(20px,3vw,28px)",margin:"0 0 6px"}}>Marketplace API</h2>
    <p style={{color:C.mut,margin:"0 0 22px",fontSize:14}}>Build on SoftSell Builder. Generate plugins programmatically.</p>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12,marginBottom:22}}>
      {[{l:"Requests",v:"847",c:C.p},{l:"Successful",v:"831",c:C.grn},{l:"Credits Left",v:"153",c:C.amb},{l:"Avg Time",v:"22s",c:C.acc}].map(s=><Card key={s.l}><div style={{fontSize:22,fontWeight:900,color:s.c}}>{s.v}</div><div style={{fontSize:11,color:C.mut,marginTop:4}}>{s.l}</div></Card>)}
    </div>
    <Card style={{marginBottom:18}}>
      <div style={{fontSize:13,fontWeight:700,color:C.txt,marginBottom:10}}>Your API Key</div>
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:8}}><code style={{flex:1,background:C.bg,border:"1px solid "+C.bdr,borderRadius:8,padding:"10px 14px",fontSize:12,color:hasAccess?C.txt:C.mut,fontFamily:"monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",filter:hasAccess?"none":"blur(4px)",userSelect:hasAccess?"text":"none"}}>{key}</code><Btn variant="sec" size="sm" onClick={copy} disabled={!hasAccess}>{cp?"Copied":"Copy"}</Btn><Btn variant="out" size="sm" disabled={!hasAccess}>Regenerate</Btn></div>
      {!hasAccess && <div style={{background:C.amb+"12", border:"1px solid "+C.amb+"30", borderRadius:8, padding:"12px 16px", marginTop:10, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10}}>
        <span style={{fontSize:12, color:C.amb, fontWeight:600}}>API access requires an Agency or Enterprise plan.</span>
        <Btn variant="acc" size="sm" onClick={()=>{if(go)go("pricing")}}>Upgrade Now</Btn>
      </div>}
      <div style={{fontSize:11,color:C.mut,marginTop:8}}>Server-side only. Authorization: Bearer {hasAccess?key.slice(0,14):"ss_live_xxxxxxxx"}...</div>
    </Card>
    <Card style={{marginBottom:18}}>
      <div style={{fontSize:13,fontWeight:700,color:C.txt,marginBottom:14}}>Endpoints</div>
      {eps.map(e=><div key={e} style={{display:"flex",gap:9,alignItems:"center",padding:"8px 0",borderBottom:"1px solid "+(C.bdr+"20")}}><Bdg text={e.includes("{id}")?"GET":"POST"} color={e.includes("{id}")?C.grn:C.p}/><code style={{fontSize:12,color:C.txt,fontFamily:"monospace"}}>{e}</code></div>)}
    </Card>
    <Card style={{marginBottom:18}}><div style={{fontSize:12,color:C.mut,marginBottom:9}}>cURL example</div><CodeV code={curlEx} lang="bash"/></Card>
    <Card>
      <div style={{fontSize:13,fontWeight:700,color:C.txt,marginBottom:14}}>Interactive API Explorer</div>
      <div style={{display:"flex",gap:10,marginBottom:12,flexWrap:"wrap"}}>
        <select value={ep} onChange={e=>{setEp(e.target.value);setResp(null);}} style={{flex:1,background:C.bg,border:"1px solid "+C.bdr,borderRadius:8,color:C.txt,fontSize:13,padding:"8px 12px",outline:"none",fontFamily:"monospace",cursor:"pointer"}}>{eps.map(e=><option key={e} value={e}>{e}</option>)}</select>
        <Btn onClick={send} disabled={loading}>{loading?<><Spin size={13} color="#fff"/> Sending...</>:"Send Request"}</Btn>
      </div>
      <div style={{display:"flex",gap:2,marginBottom:12,borderBottom:"1px solid "+C.bdr}}>
        {["request","response"].map(t=><button key={t} onClick={()=>setReqTab(t)} style={{background:"none",border:"none",cursor:"pointer",padding:"7px 14px",fontSize:12,fontWeight:600,color:reqTab===t?C.p:C.mut,borderBottom:"2px solid "+(reqTab===t?C.p:"transparent"),marginBottom:-1,fontFamily:"inherit"}}>{t[0].toUpperCase()+t.slice(1)}</button>)}
      </div>
      {reqTab==="request"&&<textarea value={body} onChange={e=>setBody(e.target.value)} rows={6} style={{width:"100%",background:"#080812",border:"1px solid "+C.bdr,borderRadius:9,color:"#CBD5E1",fontSize:12,padding:14,outline:"none",fontFamily:"monospace",boxSizing:"border-box",resize:"vertical",lineHeight:1.7}}/>}
      {reqTab==="response"&&(resp?<CodeV code={JSON.stringify(resp,null,2)} lang="JSON"/>:<div style={{padding:"24px 20px",textAlign:"center",color:C.mut,fontSize:13}}>Hit Send to see the response here.</div>)}
    </Card>
  </div>;
};

const Settings=()=>{
  const [ai,setAi]=useState("claude");
  const [sb,setSb]=useState(true);
  const [af,setAf]=useState(true);
  const [sec,setSec]=useState(true);
  const [wl,setWl]=useState(false);
  const [reg,setReg]=useState("africa-south1");
  const [saved,setSaved]=useState(false);
  const Toggle=({value,onChange,label,desc})=><div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"11px 0",borderBottom:"1px solid "+C.bdr}}><div><div style={{fontSize:13,fontWeight:600,color:C.txt}}>{label}</div>{desc&&<div style={{fontSize:11,color:C.mut,marginTop:2}}>{desc}</div>}</div><div onClick={()=>onChange(!value)} style={{width:44,height:24,borderRadius:12,background:value?C.grn:C.bdr,cursor:"pointer",position:"relative",transition:"background .2s",flexShrink:0}}><div style={{width:18,height:18,borderRadius:"50%",background:"#fff",position:"absolute",top:3,left:value?23:3,transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,.3)"}}/></div></div>;
  const Section=({title,children})=><Card style={{marginBottom:13}}><div style={{fontSize:11,color:C.mut,marginBottom:12,textTransform:"uppercase",letterSpacing:".1em",fontWeight:700,borderBottom:"1px solid "+C.bdr,paddingBottom:10}}>{title}</div>{children}</Card>;
  return <div style={{paddingBottom:48,maxWidth:620}}>
    <h2 style={{color:C.txt,fontWeight:900,fontSize:"clamp(20px,3vw,28px)",margin:"0 0 22px"}}>Settings</h2>
    <Section title="AI Engine"><div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>{[{id:"claude",l:"Claude Sonnet 4.6",s:"Anthropic - Recommended"},{id:"openai",l:"GPT-4o",s:"OpenAI"},{id:"deepseek",l:"DeepSeek V3",s:"Fast and cost-effective"},{id:"ollama",l:"Ollama Local",s:"Self-hosted, free"}].map(m=><div key={m.id} onClick={()=>setAi(m.id)} style={{padding:"10px 12px",borderRadius:9,cursor:"pointer",border:"2px solid "+(ai===m.id?C.p:C.bdr),background:ai===m.id?C.p+"10":"transparent",transition:"all .15s"}}><div style={{fontSize:12,fontWeight:700,color:C.txt}}>{m.l}</div><div style={{fontSize:10,color:C.mut,marginTop:2}}>{m.s}</div></div>)}</div></Section>
    <Section title="Pipeline"><Toggle value={sb} onChange={setSb} label="WordPress Sandbox" desc="Docker WP 6.5 + Divi 4.24 - test before delivery"/><Toggle value={af} onChange={setAf} label="Auto-Fix Agent" desc="AI repairs errors and reruns until all 12 tests pass"/><Toggle value={sec} onChange={setSec} label="Security Scanner" desc="XSS, SQLi, eval() - every generation"/><Toggle value={wl} onChange={setWl} label="White-Label Output" desc="Remove SoftSell credits from all generated code (Agency plan only)"/></Section>
    <Section title="Google Cloud"><div style={{fontSize:12,color:C.mut,marginBottom:10}}>All infrastructure on GCP. No AWS. Data stays in your region.</div><div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>{["africa-south1 (Johannesburg)","europe-west1 (Belgium)","us-central1 (Iowa)","asia-southeast1 (Singapore)"].map(r=>{const id=r.split(" ")[0];return <div key={id} onClick={()=>setReg(id)} style={{padding:"8px 12px",borderRadius:8,cursor:"pointer",fontSize:12,border:"2px solid "+(reg===id?C.p:C.bdr),background:reg===id?C.p+"10":"transparent",color:reg===id?C.txt:C.mut,transition:"all .15s"}}>{r}</div>;})}</div><div style={{display:"flex",flexWrap:"wrap",gap:5}}>{["Cloud Run","Cloud SQL","GCS (not S3)","Memorystore","Firebase Auth","Secret Manager","Cloud Build"].map(s=><Bdg key={s} text={s} color={C.p}/>)}</div></Section>
    <Section title="PayFast and Marketplace"><div style={{fontSize:13,color:C.sub,lineHeight:1.7,marginBottom:12}}>PayFast handles subscriptions and payouts. Webhook: <code style={{background:C.bg,padding:"2px 6px",borderRadius:4,fontSize:11,color:C.acc}}>{API}/api/v1/payments/payfast/notify</code></div><div style={{display:"flex",gap:8,flexWrap:"wrap"}}><Btn variant="sec" size="sm">PayFast Dashboard</Btn><Btn variant="sec" size="sm">Payout History</Btn><Btn variant="sec" size="sm" onClick={()=>window.open(MKT,"_blank")}>Marketplace</Btn></div></Section>
    <Btn size="lg" onClick={()=>{setSaved(true);setTimeout(()=>setSaved(false),2000);}} style={{minWidth:160}}>{saved?"Saved!":"Save Settings"}</Btn>
  </div>;
};

const Team=({go})=>{
  const [email,setEmail]=useState("");
  const [sent,setSent]=useState(false);
  const [role,setRole]=useState("editor");
  const members=[{name:"You",email:"me@example.com",role:"Owner",exports:0}];
  const rc={Owner:C.p,Admin:C.acc,Editor:C.grn,Viewer:C.mut};
  const invite=()=>{if(!email.trim())return;setSent(true);setTimeout(()=>{setSent(false);setEmail("");},2500);};
  return <div style={{paddingBottom:48,maxWidth:700}}>
    <h2 style={{color:C.txt,fontWeight:900,fontSize:"clamp(20px,3vw,28px)",margin:"0 0 6px"}}>Team Management</h2>
    <p style={{color:C.mut,margin:"0 0 22px",fontSize:14}}>Agency plan includes 5 seats. All members share your plan limits.</p>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12,marginBottom:20}}>
      {[{l:"Members",v:"1 / 5",c:C.p},{l:"Total Exports",v:"0",c:C.grn},{l:"Plan",v:"Agency",c:C.amb},{l:"Seats Left",v:"4",c:C.acc}].map(s=><Card key={s.l}><div style={{fontSize:22,fontWeight:900,color:s.c}}>{s.v}</div><div style={{fontSize:11,color:C.mut,marginTop:4}}>{s.l}</div></Card>)}
    </div>
    <Card style={{marginBottom:16}}>
      <div style={{fontSize:13,fontWeight:700,color:C.txt,marginBottom:12}}>Invite a Member</div>
      <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
        <Inp value={email} onChange={setEmail} placeholder="colleague@company.com" style={{flex:"1 1 200px"}}/>
        <select value={role} onChange={e=>setRole(e.target.value)} style={{background:C.bg,border:"1px solid "+C.bdr,borderRadius:9,color:C.txt,fontSize:13,padding:"11px 14px",outline:"none",fontFamily:"inherit",cursor:"pointer"}}>{["admin","editor","viewer"].map(r=><option key={r} value={r}>{r[0].toUpperCase()+r.slice(1)}</option>)}</select>
        <Btn variant="acc" onClick={invite} disabled={sent||!email.trim()}>{sent?"Sent!":"Send Invite"}</Btn>
      </div>
    </Card>
    <Card>
      <div style={{fontSize:13,fontWeight:700,color:C.txt,marginBottom:14}}>Team Members</div>
      {members.map((m,i)=><div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"11px 0",borderBottom:"1px solid "+(C.bdr+"20"),flexWrap:"wrap",gap:8}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}><div style={{width:36,height:36,borderRadius:"50%",background:"linear-gradient(135deg,"+C.p+","+C.acc+")",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:900,color:"#fff",flexShrink:0}}>{m.name[0]}</div><div><div style={{fontSize:13,fontWeight:700,color:C.txt}}>{m.name}</div><div style={{fontSize:11,color:C.mut}}>{m.email} - {m.exports} exports</div></div></div>
        <div style={{display:"flex",alignItems:"center",gap:8}}><Bdg text={m.role} color={rc[m.role]||C.mut}/>{m.pending&&<Bdg text="Pending" color={C.amb}/>}{m.role!=="Owner"&&<><Btn variant="ghost" size="sm">Edit</Btn><Btn variant="ghost" size="sm" style={{color:C.rose}}>Remove</Btn></>}</div>
      </div>)}
      <div style={{marginTop:12,fontSize:12,color:C.mut}}>Need more than 5 seats? <button onClick={()=>go("pricing")} style={{background:"none",border:"none",color:C.acc,cursor:"pointer",fontFamily:"inherit",fontSize:12,padding:0}}>Contact us for Enterprise</button></div>
    </Card>
  </div>;
};

const Profile=({user,onLogout,go})=>{
  const plan=PLANS.find(p=>p.id===(user&&user.plan||"free"))||PLANS[0];
  const used=7,limit=plan.exports===-1?null:plan.exports;
  return <div style={{paddingBottom:48,maxWidth:600}}>
    <h2 style={{color:C.txt,fontWeight:900,fontSize:"clamp(20px,3vw,28px)",margin:"0 0 22px"}}>My Account</h2>
    <Card style={{marginBottom:13}}>
      <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:16}}>
        <div style={{width:54,height:54,borderRadius:"50%",background:"linear-gradient(135deg,"+C.p+","+C.acc+")",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,fontWeight:900,color:"#fff",flexShrink:0}}>{(user&&user.name||"U")[0].toUpperCase()}</div>
        <div style={{flex:1,minWidth:0}}><div style={{fontSize:16,fontWeight:800,color:C.txt,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user&&user.name||"User"}</div><div style={{fontSize:13,color:C.mut,marginTop:2}}>{user&&user.email||""}</div><div style={{marginTop:5}}><Bdg text={plan.name+" Plan"} color={plan.color}/></div></div>
        <Btn variant="sec" size="sm">Edit Profile</Btn>
      </div>
      <div style={{background:C.bg,borderRadius:10,padding:13}}><div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:7}}><span style={{color:C.mut}}>Monthly exports</span><span style={{color:C.txt,fontWeight:700}}>{used}{limit?" / "+limit:""} {limit?"used":"(unlimited)"}</span></div>{limit&&<><PBar value={(used/limit)*100} color={used/limit>.8?C.rose:used/limit>.5?C.amb:C.grn} height={7}/><div style={{fontSize:11,color:C.mut,marginTop:5}}>{limit-used} exports remaining</div></>}</div>
    </Card>
    <Card style={{marginBottom:13}}>
      <div style={{fontSize:13,fontWeight:700,color:C.txt,marginBottom:14}}>Creator Earnings</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:16}}>{[{l:"Total Earned",v:"$284.60",c:C.grn},{l:"This Month",v:"$47.20",c:C.p},{l:"Pending",v:"$12.40",c:C.amb}].map(s=><div key={s.l} style={{background:C.bg,borderRadius:10,padding:"12px 14px",textAlign:"center"}}><div style={{fontSize:18,fontWeight:900,color:s.c}}>{s.v}</div><div style={{fontSize:10,color:C.mut,marginTop:2}}>{s.l}</div></div>)}</div>
      {[{name:"Smart Countdown Pro",sales:24,earned:"$278.40",status:"live"},{name:"ROI Calculator Plus",sales:4,earned:"$156.00",status:"review"}].map((l,i)=><div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderBottom:"1px solid "+(C.bdr+"20")}}><div><div style={{fontSize:13,fontWeight:600,color:C.txt}}>{l.name}</div><div style={{fontSize:11,color:C.mut,marginTop:2}}>{l.sales} sales - {l.earned} earned</div></div><div style={{display:"flex",gap:8,alignItems:"center"}}><Bdg text={l.status} color={l.status==="live"?C.grn:C.amb}/><Btn variant="ghost" size="sm">Edit</Btn></div></div>)}
      <div style={{marginTop:12}}><Btn variant="acc" size="sm" onClick={()=>go("generate")}>List a New Plugin</Btn></div>
    </Card>
    <Card style={{marginBottom:13}}>
      <div style={{fontSize:13,fontWeight:700,color:C.txt,marginBottom:14}}>Saved Prompts</div>
      {["Pricing calculator with tax and discount sliders for WooCommerce","Exit-intent popup with Mailchimp integration and A/B testing","Before and after slider with dark mode and mobile support"].map((p,i)=><div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderBottom:"1px solid "+(C.bdr+"20")}}><div style={{fontSize:12,color:C.sub,lineHeight:1.5,flex:1,marginRight:10}}>{p}</div><div style={{display:"flex",gap:6,flexShrink:0}}><Btn size="sm" onClick={()=>go("generate",p)}>Generate</Btn><Btn variant="ghost" size="sm">X</Btn></div></div>)}
    </Card>
    <Card>
      <div style={{fontSize:13,fontWeight:700,color:C.txt,marginBottom:12}}>Account</div>
      <div style={{display:"flex",gap:9,flexWrap:"wrap"}}>
        <Btn variant="sec" size="sm">Change Email</Btn>
        <Btn variant="sec" size="sm">Change Password</Btn>
        <Btn variant="sec" size="sm" onClick={()=>go("pricing")}>Upgrade Plan</Btn>
        <button onClick={onLogout} style={{background:C.rose,color:"#fff",border:"none",fontSize:12,padding:"6px 14px",borderRadius:8,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Sign Out</button>
      </div>
    </Card>
  </div>;
};

const NAV=[{id:"dashboard",label:"Dashboard"},{id:"generate",label:"Generate"},{id:"library",label:"Templates"},{id:"toolkit",label:"Free Toolkit"},{id:"marketplace",label:"Marketplace"},{id:"divi",label:"Divi Connector"},{id:"sandbox",label:"WP Sandbox"},{id:"api",label:"API"},{id:"pricing",label:"Pricing"},{id:"team",label:"Team"}];

export default function App(){
  const initPage = new URLSearchParams(window.location.search).get("page") || "generate";
  const [page,setPage]=useState(initPage);
  const [boot,setBoot]=useState("");
  const isIframe = window.self !== window.top;
  const go=(p,prompt)=>{if(prompt)setBoot(prompt);setPage(p);};

  // Handle direct URL routes — e.g. /download/free-toolkit
  useEffect(()=>{
    const path = window.location.pathname;
    if(path === '/download/free-toolkit'){
      // Trigger the ZIP download immediately
      downloadToolkitZip();
      // Replace history so the back button works cleanly
      window.history.replaceState({},'','/');
      // Show the toolkit page so user sees what they downloaded
      setPage('toolkit');
    } else if(path === '/download/divi-connector'){
      downloadDiviConnectorZip();
      window.history.replaceState({},'','/');
      setPage('divi');
    }
  },[]);

  const render=()=>{switch(page){case"dashboard":return <Dashboard go={go}/>;case"generate":return <Generator initPrompt={boot} go={go}/>;case"library":return <Library go={go}/>;case"toolkit":return <Toolkit go={go}/>;case"marketplace":return <Marketplace/>;case"divi":return <DiviConn go={go}/>;case"sandbox":return <Sandbox initPlugin={boot}/>;case"agents":return <Agents/>;case"api":return <ApiScreen go={go}/>;case"pricing":return <Pricing go={go}/>;case"settings":return <Settings/>;case"team":return <Team go={go}/>;default:return <Dashboard go={go}/>;} };
  return <div style={{minHeight:"100vh",background:C.bg,color:C.txt,fontFamily:"Inter,system-ui,sans-serif",display:"flex",flexDirection:"column"}}>
    {!isIframe && <header style={{position:"sticky",top:0,zIndex:200,background:C.sur+"F2",backdropFilter:"blur(16px)",borderBottom:"1px solid "+C.bdr,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 20px",height:56}}>
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        <div style={{width:32,height:32,borderRadius:9,background:"linear-gradient(135deg,"+C.p+","+C.acc+")",display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,color:"#fff",fontWeight:900}}>S</div>
        <div><span style={{fontSize:15,fontWeight:900,color:C.txt}}>SoftSell <span style={{color:C.p}}>Builder</span></span><div style={{fontSize:9,color:C.mut,letterSpacing:".1em",textTransform:"uppercase",marginTop:-2}}>The Stripe of Builder Extensions</div></div>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <Bdg text="Live" color={C.grn}/>
        <Btn variant="acc" size="sm" onClick={()=>window.open(MKT,"_blank")}>Marketplace</Btn>
      </div>
    </header>}
    <div style={{display:"flex",flex:1}}>
      {!isIframe && <nav style={{width:200,flexShrink:0,background:C.sur,borderRight:"1px solid "+C.bdr,padding:"12px 0 20px",overflowY:"auto"}}>
        {NAV.map(item=><button key={item.id} onClick={()=>setPage(item.id)} style={{display:"flex",alignItems:"center",width:"100%",padding:"9px 16px",background:"none",borderTop:"none",borderRight:"none",borderBottom:"none",borderLeft:"3px solid "+(page===item.id?C.p:"transparent"),color:page===item.id?C.p:C.mut,fontSize:13,fontWeight:page===item.id?700:500,cursor:"pointer",textAlign:"left",fontFamily:"inherit",transition:"all .12s"}}>{item.label}</button>)}
        <div style={{margin:"14px 11px 0",padding:"10px 13px",background:C.card,borderRadius:10,border:"1px solid "+C.bdr}}><div style={{fontSize:9,color:C.mut,marginBottom:5,textTransform:"uppercase",letterSpacing:".1em"}}>Adapters</div><div style={{fontSize:12,fontWeight:700,color:C.txt}}>9 / 9 Online</div><div style={{fontSize:10,color:C.grn,marginTop:2}}>All platforms active</div></div>
      </nav>}
      <main style={{flex:1,overflowY:"auto",padding:isIframe?"16px":"24px 24px 0",minWidth:0}}>{render()}</main>
    </div>
    <style>{"*,*::before,*::after{box-sizing:border-box}body{margin:0}textarea:focus,input:focus,select:focus{border-color:#7C3AED;box-shadow:0 0 0 3px rgba(124,58,237,.18)}@keyframes spin{to{transform:rotate(360deg)}}@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}::-webkit-scrollbar{width:5px;height:5px}::-webkit-scrollbar-track{background:#06060E}::-webkit-scrollbar-thumb{background:#22223A;border-radius:3px}a{color:#06B6D4}@media(max-width:680px){nav{display:none}main{padding:14px 14px 0}}"}</style>
  </div>;
}
